/**
 * Atmos Air Quality – UI-Texte für Custom-Tabs.
 * Wird in i18n/index.ts in alle Locales gemerged (fehlende Sprache → EN).
 */
export const aqproEn = {
    views: {
        intro: 'Atmos Air Quality shows current air quality and forecasts as a Garmin widget. Swipe or use Up/Down to move between the pages you enabled in settings.',
        current: {
            title: 'Current air quality',
            gauge: 'Large coloured gauge: current AQI (EU or US, depending on your setting). The marker shows where you are on the scale.',
            category: 'Category label (e.g. Good / Moderate / Poor) under or next to the value.',
            pollutants: 'PM2.5, PM10 and O₃ values are shown as quick reference.',
            location: 'Location name and how long ago the data was updated.',
        },
        hourly: {
            title: '24-hour forecast',
            bars: 'Bar chart of AQI for the next 24 hours, starting from the current hour.',
            colors: 'Bar colours follow the same AQI categories as the main gauge.',
            axis: 'Time runs left → right (now → later).',
        },
        daily: {
            title: '5-day forecast',
            range: 'Each day shows the expected AQI maximum and minimum.',
            use: 'Useful for planning outdoor activities over the coming days.',
        },
        dust: {
            title: 'Saharan dust / dust',
            current: 'Current dust level plus a 24-hour trend.',
            daily: 'Multi-day dust outlook when data is available.',
            note: 'Especially relevant during Saharan dust events that raise PM levels.',
        },
        particles: {
            title: 'Particle comparison',
            dual: 'Dual chart comparing two series you choose in settings (e.g. PM2.5 vs PM10, or O₃ vs NO₂).',
            single: 'If only one series is selected, it uses the full width.',
            series: 'Available series: PM2.5, PM10, Dust, UV, O₃, NO₂, CO, SO₂, AOD.',
        },
        visibility: {
            title: 'Haze / visibility (AOD)',
            aod: 'Based on Aerosol Optical Depth (AOD) – how much aerosols dim the atmosphere.',
            km: 'Converted into an approximate visibility in kilometres.',
            chart: '24-hour chart: taller bars ≈ better visibility; colour indicates haze density.',
        },
        pollen: {
            title: 'Pollen (Europe)',
            species: 'Six types: alder, birch, grass, mugwort, olive, ragweed – each with a level bar.',
            chart: 'Bottom chart: 24-hour maximum across all types.',
            europe: 'Data comes from CAMS and is typically available in Europe only. Outside Europe you may see “No data”.',
        },
        glance: {
            title: 'Glance view',
            what: 'Compact watch face glance: current AQI value, category and a colour scale with marker.',
            mono: 'On monochrome Instinct watches a progress bar is used instead of the colour scale.',
        },
    },
    controls: {
        nav: {
            title: 'Navigate between pages',
            swipe: 'Touchscreen: swipe left/right (or up/down, depending on the watch) to change pages.',
            buttons: 'Button watches: Up / Down or Next / Previous Page.',
            order: 'Only pages you assigned in settings (Page 1–6) appear. Empty slots (“None”) are skipped.',
        },
        refresh: {
            title: 'Manual refresh',
            text: 'Press Select / Enter / Start to request fresh data immediately. The watch needs a phone connection (Bluetooth) for the download.',
            bg: 'In the background the app also updates automatically using the interval from settings (default 60 minutes).',
        },
        menu: {
            title: 'On-watch menu (AQI standard)',
            text: 'Open the menu on the watch to switch between European AQI and US AQI. This writes the same setting as in Connect IQ Mobile – whichever you change last wins.',
        },
        location: {
            title: 'Location',
            text: 'By default the app uses your watch/phone location. You can override it with alternative coordinates in settings (useful if you want air quality for another place).',
            gps: 'If location is missing or outdated, get a GPS fix (start an activity briefly or use the GPS widget), then refresh.',
        },
        connection: {
            title: 'Phone connection',
            text: 'Internet requests go through the paired phone. If you see a connection error, open Garmin Connect, keep Bluetooth on, and try a manual refresh.',
        },
    },
    index: {
        intro: {
            title: 'Two standards – one app',
            text: 'Atmos Air Quality can show either the European Air Quality Index (EAQI) or the US AQI (EPA). The number, colour scale and category labels change with the standard – the underlying pollutant measurements stay the same.',
            switch: 'Switch in Connect IQ settings (“AQI standard”) or via the on-watch menu.',
        },
        diff: {
            title: 'Main differences',
            scale: 'Scale: EU typically runs with lower absolute numbers (gauge ends around 120). US AQI commonly goes up to 300+ on the gauge.',
            thresholds: 'Category thresholds differ – the same air can look “moderate” in one system and “unhealthy for sensitive groups” in the other.',
            use: 'Use EU if you are used to European apps/media; use US if you compare with US EPA / AirNow-style reports.',
        },
        table: { range: 'Index', category: 'Category' },
        eu: {
            title: 'European AQI (EAQI)',
            text: 'Combines several pollutants into one index. Categories roughly: Good → Fair → Moderate → Poor → Very Poor → Extremely Poor.',
            c0: 'Good',
            c1: 'Fair',
            c2: 'Moderate',
            c3: 'Poor',
            c4: 'Very Poor',
            c5: 'Extremely Poor',
        },
        us: {
            title: 'US AQI (EPA)',
            text: 'Widely used in the US. Categories: Good → Moderate → Unhealthy for Sensitive Groups → Unhealthy → Very Unhealthy → Hazardous.',
            c0: 'Good',
            c1: 'Moderate',
            c2: 'Unhealthy for Sensitive Groups',
            c3: 'Unhealthy',
            c4: 'Very Unhealthy',
            c5: 'Hazardous',
        },
        warning: 'Do not mix the two numbers. An “80” on the EU scale is not the same as an “80” on the US scale.',
    },
    values: {
        intro: {
            title: 'What do the values mean?',
            text: 'The AQI is a summary index. The pollutant pages and particle chart let you see which component is elevated. Units for particles and gases are typically µg/m³ unless noted otherwise.',
        },
        pm25: {
            title: 'PM2.5',
            text: 'Fine particles smaller than 2.5 µm. They can penetrate deep into the lungs. Often the main driver of a bad AQI in cities and during wood smoke / wildfire events.',
        },
        pm10: {
            title: 'PM10',
            text: 'Coarser particles up to 10 µm (dust, pollen fragments, road abrasion). Less deep into the lungs than PM2.5, but still relevant for respiratory irritation.',
        },
        o3: {
            title: 'Ozone (O₃)',
            text: 'Ground-level ozone forms from traffic/industry gases in sunlight. Often higher on sunny afternoons; can irritate airways during outdoor sports.',
        },
        no2: {
            title: 'Nitrogen dioxide (NO₂)',
            text: 'Typically linked to traffic and combustion. Elevated near busy roads; part of the European index calculation.',
        },
        so2: {
            title: 'Sulphur dioxide (SO₂)',
            text: 'From burning sulphur-containing fuels and some industrial sources. Usually lower in many Western European cities than PM or NO₂.',
        },
        co: {
            title: 'Carbon monoxide (CO)',
            text: 'Incomplete combustion (traffic, heating). Dangerous in high indoor concentrations; outdoor levels are usually much lower.',
        },
        dust: {
            title: 'Dust',
            text: 'Mineral / Saharan dust load. Spikes can raise PM values across large regions even far from the source.',
        },
        uv: {
            title: 'UV index',
            text: 'Sunburn risk index (not an air pollutant). Handy next to outdoor air-quality planning on clear days.',
        },
        aod: {
            title: 'AOD / visibility',
            text: 'Aerosol Optical Depth describes how “hazy” the atmosphere is. The app maps it to an approximate visibility in km (clear → dense haze).',
        },
        pollen: {
            title: 'Pollen',
            text: 'Allergy-relevant pollen counts (Europe). Levels are shown per species; the chart tracks the daily peak across types.',
        },
        disclaimer: 'This page is educational, not medical advice. For health decisions follow local official air-quality guidance.',
    },
    settings: {
        intro: {
            title: 'Settings overview',
            text: 'Open the app settings in Garmin Connect IQ (phone) or Garmin Express. The AQI standard can also be changed from the on-watch menu.',
        },
        aqStd: {
            title: 'AQI standard',
            text: 'European or US. Affects the number, colours and category labels everywhere (main view, charts, glance, complications).',
        },
        pages: {
            title: 'Pages 1–6',
            text: 'Each slot can be one of: Current, 24h forecast, 5-day forecast, Dust, Particles, Haze/visibility, Pollen – or None to hide the slot.',
            tip: 'Tip: put Current first, then the forecasts you use most. Hide unused pages to swipe less.',
        },
        series: {
            title: 'Particle series A / B',
            text: 'Choose which two metrics the particle comparison chart shows (PM2.5, PM10, Dust, UV, O₃, NO₂, CO, SO₂, AOD). Set a series to “None” to hide it; one series uses the full chart width.',
        },
        location: {
            title: 'Alternative location',
            text: 'Optional latitude / longitude. Leave at 0 / 0 to use the watch location. Set both to pin a fixed place (home, workplace, holiday spot).',
        },
        interval: {
            title: 'Update interval',
            text: 'How often the background service should refresh (minutes). Minimum 10, maximum 720. Default 60. Shorter intervals use more phone data and battery.',
        },
        comp: {
            title: 'Complications',
            forDevs: 'For developers:',
            intro: 'This app publishes complications for use in watch faces',
            aqi: '"AtmosAirAQI": Current air quality index (EU or US index, depending on the user’s selection)',
            aqiRange: 'Includes the “Range” indicating where this value falls.',
            ts: '"AtmosAirTs": UTC timestamp of the current value',
            longLabel: 'Long Label: “AtmosAirAQI”',
        },
        limits: {
            title: 'Device limits (low memory)',
            warning: 'On some older / low-memory models (e.g. Instinct 2, Forerunner 55, Fenix 6 and similar) the extra pages Dust, Particles, Haze and Pollen are not available. Current + forecast pages remain.',
            text: 'This is intentional so the widget stays stable within Garmin’s memory limits on those devices.',
        },
        supportBefore: 'Questions or issues? Please use the',
        supportLink: 'contact form',
        supportAfter: '.',
    },
};
export const aqproDe = {
    views: {
        intro: 'Atmos Air Quality zeigt aktuelle Luftqualität und Vorhersagen als Garmin-Widget. Wische oder nutze Hoch/Runter, um zwischen den in den Einstellungen aktivierten Seiten zu wechseln.',
        current: {
            title: 'Aktuelle Luftqualität',
            gauge: 'Großer farbiger Kreisbalken: aktueller AQI (EU oder US je nach Einstellung). Der Marker zeigt die Position auf der Skala.',
            category: 'Kategorie (z. B. Gut / Mäßig / Schlecht) unter oder neben dem Wert.',
            pollutants: 'PM2.5, PM10 und O₃ als Schnellübersicht.',
            location: 'Standortname und wie lange die Daten her sind.',
        },
        hourly: {
            title: '24-Stunden-Vorhersage',
            bars: 'Balkendiagramm des AQI für die nächsten 24 Stunden ab der aktuellen Stunde.',
            colors: 'Balkenfarben folgen denselben AQI-Kategorien wie die Hauptanzeige.',
            axis: 'Die Zeit läuft links → rechts (jetzt → später).',
        },
        daily: {
            title: '5-Tage-Vorhersage',
            range: 'Pro Tag erwartetes AQI-Maximum und -Minimum.',
            use: 'Hilfreich zur Planung von Outdoor-Aktivitäten in den nächsten Tagen.',
        },
        dust: {
            title: 'Saharastaub / Staub',
            current: 'Aktueller Staubwert plus 24-Stunden-Verlauf.',
            daily: 'Mehrtägige Staub-Prognose, wenn Daten verfügbar sind.',
            note: 'Besonders relevant bei Saharastaub-Ereignissen, die PM-Werte anheben.',
        },
        particles: {
            title: 'Partikel-Vergleich',
            dual: 'Doppel-Chart mit zwei Serien aus den Einstellungen (z. B. PM2.5 vs. PM10 oder O₃ vs. NO₂).',
            single: 'Bei nur einer Serie wird die volle Breite genutzt.',
            series: 'Verfügbare Serien: PM2.5, PM10, Staub, UV, O₃, NO₂, CO, SO₂, AOD.',
        },
        visibility: {
            title: 'Dunst / Sichtweite (AOD)',
            aod: 'Basiert auf der Aerosol Optical Depth (AOD) – wie stark Aerosole die Atmosphäre trüben.',
            km: 'Umrechnung in eine ungefähre Sichtweite in Kilometern.',
            chart: '24h-Chart: höhere Balken ≈ bessere Sicht; Farbe zeigt Dunststufe.',
        },
        pollen: {
            title: 'Pollen (Europa)',
            species: 'Sechs Arten: Erle, Birke, Gras, Beifuß, Olive, Ambrosia – jeweils mit Balken.',
            chart: 'Unten: 24h-Maximum über alle Arten.',
            europe: 'Daten kommen von CAMS und sind typischerweise nur in Europa verfügbar. Außerhalb erscheint oft „Keine Daten“.',
        },
        glance: {
            title: 'Glance-Ansicht',
            what: 'Kompakte Glance: aktueller AQI, Kategorie und Farbskala mit Marker.',
            mono: 'Auf monochromen Instinct-Uhren erscheint ein Fortschrittsbalken statt der Farbskala.',
        },
    },
    controls: {
        nav: {
            title: 'Zwischen Seiten navigieren',
            swipe: 'Touchscreen: nach links/rechts (oder hoch/runter) wischen.',
            buttons: 'Tastenuhren: Hoch / Runter bzw. Nächste / Vorherige Seite.',
            order: 'Nur die in den Einstellungen (Seite 1–6) gewählten Seiten erscheinen. „Keine“ wird übersprungen.',
        },
        refresh: {
            title: 'Manuell aktualisieren',
            text: 'Select / Enter / Start drücken, um sofort neue Daten anzufordern. Dafür braucht die Uhr eine Telefonverbindung (Bluetooth).',
            bg: 'Im Hintergrund aktualisiert die App außerdem automatisch mit dem Intervall aus den Einstellungen (Standard 60 Minuten).',
        },
        menu: {
            title: 'Menü auf der Uhr (AQI-Standard)',
            text: 'Über das Menü auf der Uhr zwischen europäischem und US-AQI wechseln. Das ist dieselbe Einstellung wie in Connect IQ Mobile – die letzte Änderung gilt.',
        },
        location: {
            title: 'Standort',
            text: 'Standardmäßig nutzt die App den Standort von Uhr/Telefon. In den Einstellungen kannst du Alternativ-Koordinaten setzen (z. B. für einen anderen Ort).',
            gps: 'Fehlt der Standort oder ist er veraltet: GPS-Fix holen (kurz Aktivität starten oder GPS-Widget), dann aktualisieren.',
        },
        connection: {
            title: 'Telefonverbindung',
            text: 'Internet-Anfragen laufen über das gekoppelte Telefon. Bei Verbindungsfehlern: Garmin Connect öffnen, Bluetooth an, manuell aktualisieren.',
        },
    },
    index: {
        intro: {
            title: 'Zwei Standards – eine App',
            text: 'Atmos Air Quality kann den europäischen Luftqualitätsindex (EAQI) oder den US-AQI (EPA) anzeigen. Zahl, Farbskala und Kategorien ändern sich – die zugrunde liegenden Schadstoffmessungen bleiben gleich.',
            switch: 'Umschalten in den Connect-IQ-Einstellungen („AQI-Standard“) oder über das Menü auf der Uhr.',
        },
        diff: {
            title: 'Wichtigste Unterschiede',
            scale: 'Skala: EU arbeitet mit niedrigeren Absolutwerten (Gauge endet etwa bei 120). US-AQI geht auf dem Gauge oft bis 300+.',
            thresholds: 'Kategorien-Schwellen unterscheiden sich – dieselbe Luft kann in einem System „mäßig“ und im anderen „ungesund für empfindliche Gruppen“ wirken.',
            use: 'EU nutzen, wenn du europäische Apps/Medien kennst; US, wenn du mit EPA-/AirNow-Angaben vergleichst.',
        },
        table: { range: 'Index', category: 'Kategorie' },
        eu: {
            title: 'Europäischer AQI (EAQI)',
            text: 'Fasst mehrere Schadstoffe zu einem Index zusammen. Kategorien grob: Gut → Befriedigend → Mäßig → Schlecht → Sehr schlecht → Extrem schlecht.',
            c0: 'Gut',
            c1: 'Befriedigend',
            c2: 'Mäßig',
            c3: 'Schlecht',
            c4: 'Sehr schlecht',
            c5: 'Extrem schlecht',
        },
        us: {
            title: 'US-AQI (EPA)',
            text: 'In den USA üblich. Kategorien: Gut → Mäßig → Ungesund für empfindliche Gruppen → Ungesund → Sehr ungesund → Gefährlich.',
            c0: 'Gut',
            c1: 'Mäßig',
            c2: 'Ungesund für empfindliche Gruppen',
            c3: 'Ungesund',
            c4: 'Sehr ungesund',
            c5: 'Gefährlich',
        },
        warning: 'Die beiden Zahlen nicht vermischen. Ein „80“ auf der EU-Skala ist nicht dasselbe wie ein „80“ auf der US-Skala.',
    },
    values: {
        intro: {
            title: 'Was bedeuten die Werte?',
            text: 'Der AQI ist ein zusammengefasster Index. Über Schadstoffseiten und Partikel-Chart siehst du, welche Komponente erhöht ist. Einheiten für Partikel und Gase sind typischerweise µg/m³.',
        },
        pm25: {
            title: 'PM2.5',
            text: 'Feinstaub unter 2,5 µm. Dringt tief in die Lunge ein. Häufig Haupttreiber eines schlechten AQI in Städten sowie bei Holzrauch / Waldbränden.',
        },
        pm10: {
            title: 'PM10',
            text: 'Gröbere Partikel bis 10 µm (Staub, Pollenbruchstücke, Straßenabrieb). Weniger tief als PM2.5, aber relevant für Atemwegsreizungen.',
        },
        o3: {
            title: 'Ozon (O₃)',
            text: 'Bodennahes Ozon entsteht aus Verkehrs-/Industrieabgasen bei Sonne. Oft nachmittags höher; kann bei Sport im Freien die Atemwege reizen.',
        },
        no2: {
            title: 'Stickstoffdioxid (NO₂)',
            text: 'Typisch durch Verkehr und Verbrennung. Erhöht an stark befahrenen Straßen; Bestandteil der europäischen Indexberechnung.',
        },
        so2: {
            title: 'Schwefeldioxid (SO₂)',
            text: 'Aus schwefelhaltigen Brennstoffen und manchen Industriequellen. In vielen westeuropäischen Städten meist niedriger als PM oder NO₂.',
        },
        co: {
            title: 'Kohlenmonoxid (CO)',
            text: 'Unvollständige Verbrennung (Verkehr, Heizen). Indoor in hohen Konzentrationen gefährlich; outdoor meist deutlich niedriger.',
        },
        dust: {
            title: 'Staub',
            text: 'Mineralischer / Saharastaub. Spitzen können PM-Werte über große Regionen hinweg anheben.',
        },
        uv: {
            title: 'UV-Index',
            text: 'Sonnenbrandrisiko (kein Luftschadstoff). Praktisch neben der Luftqualität an klaren Tagen.',
        },
        aod: {
            title: 'AOD / Sichtweite',
            text: 'Die Aerosol Optical Depth beschreibt, wie „dunstig“ die Atmosphäre ist. Die App rechnet grob in Sichtweite in km um (klar → dichter Dunst).',
        },
        pollen: {
            title: 'Pollen',
            text: 'Allergierelevante Pollenwerte (Europa). Pro Art angezeigt; das Chart zeigt das Tagesmaximum über alle Arten.',
        },
        disclaimer: 'Diese Seite dient der Information, nicht der medizinischen Beratung. Bei Gesundheitsfragen den offiziellen lokalen Luftqualitätsmeldungen folgen.',
    },
    settings: {
        intro: {
            title: 'Einstellungen im Überblick',
            text: 'Einstellungen in Garmin Connect IQ (Telefon) oder Garmin Express öffnen. Den AQI-Standard kannst du auch über das Menü auf der Uhr ändern.',
        },
        aqStd: {
            title: 'AQI-Standard',
            text: 'Europa oder USA. Beeinflusst Zahl, Farben und Kategorien überall (Hauptansicht, Charts, Glance, Complications).',
        },
        pages: {
            title: 'Seiten 1–6',
            text: 'Jeder Slot: Aktuell, 24h-Vorhersage, 5-Tage-Vorhersage, Staub, Partikel, Dunst/Sicht, Pollen – oder Keine zum Ausblenden.',
            tip: 'Tipp: Aktuell zuerst, dann die meistgenutzten Vorhersagen. Ungenutzte Seiten ausblenden spart Wischen.',
        },
        series: {
            title: 'Partikel-Serie A / B',
            text: 'Zwei Messgrößen für den Vergleichschart (PM2.5, PM10, Staub, UV, O₃, NO₂, CO, SO₂, AOD). „Keine“ blendet eine Serie aus; eine Serie nutzt die volle Breite.',
        },
        location: {
            title: 'Alternativer Standort',
            text: 'Optionale Breite / Länge. Bei 0 / 0 gilt der Uhr-Standort. Beide setzen, um einen festen Ort zu pinnen (Zuhause, Arbeit, Urlaub).',
        },
        interval: {
            title: 'Update-Intervall',
            text: 'Wie oft der Hintergrunddienst aktualisieren soll (Minuten). Minimum 10, Maximum 720. Standard 60. Kürzer = mehr Datenverbrauch und Akku.',
        },
        comp: {
            title: 'Complications',
            forDevs: 'Für Entwickler:',
            intro: 'Diese App stellt Complications für die Nutzung in Watchfaces bereit',
            aqi: '"AtmosAirAQI": Aktueller Luftqualitätsindex (EU- oder US-Index, je nach Auswahl des Nutzers)',
            aqiRange: 'Enthält den „Range“, der anzeigt, wo dieser Wert liegt.',
            ts: '"AtmosAirTs": UTC-Zeitstempel des aktuellen Werts',
            longLabel: 'Long Label: „AtmosAirAQI“',
        },
        limits: {
            title: 'Geräte-Limits (wenig Speicher)',
            warning: 'Auf manchen älteren / speicherarmen Modellen (z. B. Instinct 2, Forerunner 55, Fenix 6 u. ä.) fehlen die Extra-Seiten Staub, Partikel, Dunst und Pollen. Aktuell + Vorhersagen bleiben.',
            text: 'Das ist Absicht, damit das Widget innerhalb der Garmin-Speicherlimits stabil bleibt.',
        },
        supportBefore: 'Fragen oder Probleme? Bitte nutze das',
        supportLink: 'Kontaktformular',
        supportAfter: '.',
    },
};
export const aqproFr = {
    views: {
        intro: 'Atmos Air Quality affiche la qualité de l\'air actuelle et les prévisions sous forme de widget Garmin. Balayez ou utilisez Haut/Bas pour naviguer entre les pages activées dans les réglages.',
        current: {
            title: 'Qualité de l\'air actuelle',
            gauge: 'Grand cadran coloré : AQI actuel (UE ou US selon votre réglage). Le marqueur indique votre position sur l\'échelle.',
            category: 'Libellé de catégorie (p. ex. Bon / Modéré / Mauvais) sous ou à côté de la valeur.',
            pollutants: 'Les valeurs PM2.5, PM10 et O₃ sont affichées comme référence rapide.',
            location: 'Nom du lieu et ancienneté des données.',
        },
        hourly: {
            title: 'Prévision sur 24 heures',
            bars: 'Diagramme en barres de l\'AQI pour les 24 prochaines heures, à partir de l\'heure actuelle.',
            colors: 'Les couleurs des barres suivent les mêmes catégories AQI que le cadran principal.',
            axis: 'Le temps va de gauche → droite (maintenant → plus tard).',
        },
        daily: {
            title: 'Prévision sur 5 jours',
            range: 'Chaque jour affiche le maximum et le minimum AQI attendus.',
            use: 'Utile pour planifier les activités en plein air dans les jours à venir.',
        },
        dust: {
            title: 'Poussière saharienne / poussière',
            current: 'Niveau de poussière actuel plus tendance sur 24 heures.',
            daily: 'Perspective poussière sur plusieurs jours lorsque les données sont disponibles.',
            note: 'Particulièrement pertinent lors d\'événements de poussière saharienne qui augmentent les niveaux de PM.',
        },
        particles: {
            title: 'Comparaison des particules',
            dual: 'Graphique double comparant deux séries choisies dans les réglages (p. ex. PM2.5 vs PM10, ou O₃ vs NO₂).',
            single: 'Si une seule série est sélectionnée, elle utilise toute la largeur.',
            series: 'Séries disponibles : PM2.5, PM10, Poussière, UV, O₃, NO₂, CO, SO₂, AOD.',
        },
        visibility: {
            title: 'Brume / visibilité (AOD)',
            aod: 'Basé sur l\'Aerosol Optical Depth (AOD) – à quel point les aérosols assombrissent l\'atmosphère.',
            km: 'Converti en visibilité approximative en kilomètres.',
            chart: 'Graphique 24 h : barres plus hautes ≈ meilleure visibilité ; la couleur indique la densité de brume.',
        },
        pollen: {
            title: 'Pollen (Europe)',
            species: 'Six types : aulne, bouleau, graminées, armoise, olivier, ambroisie – chacun avec une barre de niveau.',
            chart: 'Graphique du bas : maximum sur 24 h pour tous les types.',
            europe: 'Les données proviennent de CAMS et sont généralement disponibles en Europe uniquement. En dehors de l\'Europe, vous pouvez voir « Aucune donnée ».',
        },
        glance: {
            title: 'Vue Glance',
            what: 'Glance compact sur le cadran : valeur AQI actuelle, catégorie et échelle de couleurs avec marqueur.',
            mono: 'Sur les montres Instinct monochromes, une barre de progression remplace l\'échelle de couleurs.',
        },
    },
    controls: {
        nav: {
            title: 'Naviguer entre les pages',
            swipe: 'Écran tactile : balayer gauche/droite (ou haut/bas selon la montre) pour changer de page.',
            buttons: 'Montres à boutons : Haut / Bas ou Page suivante / précédente.',
            order: 'Seules les pages assignées dans les réglages (Page 1–6) apparaissent. Les emplacements vides (« Aucune ») sont ignorés.',
        },
        refresh: {
            title: 'Actualisation manuelle',
            text: 'Appuyez sur Select / Enter / Start pour demander des données fraîches immédiatement. La montre a besoin d\'une connexion téléphone (Bluetooth) pour le téléchargement.',
            bg: 'En arrière-plan, l\'app se met aussi à jour automatiquement selon l\'intervalle des réglages (60 minutes par défaut).',
        },
        menu: {
            title: 'Menu sur la montre (standard AQI)',
            text: 'Ouvrez le menu sur la montre pour basculer entre l\'AQI européen et l\'AQI US. Cela écrit le même réglage que dans Connect IQ Mobile – la dernière modification l\'emporte.',
        },
        location: {
            title: 'Localisation',
            text: 'Par défaut, l\'app utilise la position de la montre/téléphone. Vous pouvez la remplacer par des coordonnées alternatives dans les réglages (utile pour la qualité de l\'air d\'un autre lieu).',
            gps: 'Si la position manque ou est obsolète, obtenez un fix GPS (démarrez brièvement une activité ou utilisez le widget GPS), puis actualisez.',
        },
        connection: {
            title: 'Connexion téléphone',
            text: 'Les requêtes Internet passent par le téléphone couplé. En cas d\'erreur de connexion, ouvrez Garmin Connect, gardez le Bluetooth activé et essayez une actualisation manuelle.',
        },
    },
    index: {
        intro: {
            title: 'Deux standards – une app',
            text: 'Atmos Air Quality peut afficher l\'indice européen de qualité de l\'air (EAQI) ou l\'AQI US (EPA). Le chiffre, l\'échelle de couleurs et les catégories changent selon le standard – les mesures de polluants sous-jacentes restent les mêmes.',
            switch: 'Basculer dans les réglages Connect IQ (« Standard AQI ») ou via le menu sur la montre.',
        },
        diff: {
            title: 'Principales différences',
            scale: 'Échelle : l\'UE utilise généralement des valeurs absolues plus basses (le cadran se termine vers 120). L\'AQI US monte souvent jusqu\'à 300+ sur le cadran.',
            thresholds: 'Les seuils de catégories diffèrent – la même air peut paraître « modéré » dans un système et « malsain pour les groupes sensibles » dans l\'autre.',
            use: 'Utilisez l\'UE si vous êtes habitué aux apps/médias européens ; l\'US si vous comparez avec les rapports EPA / AirNow.',
        },
        table: { range: 'Indice', category: 'Catégorie' },
        eu: {
            title: 'AQI européen (EAQI)',
            text: 'Combine plusieurs polluants en un seul indice. Catégories approximatives : Bon → Passable → Modéré → Mauvais → Très mauvais → Extrêmement mauvais.',
            c0: 'Bon',
            c1: 'Passable',
            c2: 'Modéré',
            c3: 'Mauvais',
            c4: 'Très mauvais',
            c5: 'Extrêmement mauvais',
        },
        us: {
            title: 'AQI US (EPA)',
            text: 'Largement utilisé aux États-Unis. Catégories : Good → Moderate → Unhealthy for Sensitive Groups → Unhealthy → Very Unhealthy → Hazardous.',
            c0: 'Good',
            c1: 'Moderate',
            c2: 'Unhealthy for Sensitive Groups',
            c3: 'Unhealthy',
            c4: 'Very Unhealthy',
            c5: 'Hazardous',
        },
        warning: 'Ne mélangez pas les deux chiffres. Un « 80 » sur l\'échelle UE n\'est pas la même chose qu\'un « 80 » sur l\'échelle US.',
    },
    values: {
        intro: {
            title: 'Que signifient les valeurs ?',
            text: 'L\'AQI est un indice de synthèse. Les pages polluants et le graphique particules permettent de voir quelle composante est élevée. Les unités pour particules et gaz sont typiquement µg/m³ sauf indication contraire.',
        },
        pm25: {
            title: 'PM2.5',
            text: 'Particules fines inférieures à 2,5 µm. Elles pénètrent profondément dans les poumons. Souvent le principal facteur d\'un mauvais AQI en ville et lors de fumée de bois / incendies.',
        },
        pm10: {
            title: 'PM10',
            text: 'Particules plus grosses jusqu\'à 10 µm (poussière, fragments de pollen, abrasion routière). Moins profondes que PM2.5, mais pertinentes pour les irritations respiratoires.',
        },
        o3: {
            title: 'Ozone (O₃)',
            text: 'L\'ozone au sol se forme à partir des gaz de trafic/industrie au soleil. Souvent plus élevé l\'après-midi ensoleillé ; peut irriter les voies respiratoires lors du sport en plein air.',
        },
        no2: {
            title: 'Dioxyde d\'azote (NO₂)',
            text: 'Typiquement lié au trafic et à la combustion. Élevé près des routes chargées ; fait partie du calcul de l\'indice européen.',
        },
        so2: {
            title: 'Dioxyde de soufre (SO₂)',
            text: 'Provenant de la combustion de combustibles soufrés et de certaines sources industrielles. Généralement plus bas que PM ou NO₂ dans de nombreuses villes d\'Europe occidentale.',
        },
        co: {
            title: 'Monoxyde de carbone (CO)',
            text: 'Combustion incomplète (trafic, chauffage). Dangereux en forte concentration intérieure ; les niveaux extérieurs sont généralement bien plus bas.',
        },
        dust: {
            title: 'Poussière',
            text: 'Charge de poussière minérale / saharienne. Les pics peuvent augmenter les valeurs PM sur de vastes régions, loin de la source.',
        },
        uv: {
            title: 'Indice UV',
            text: 'Indice de risque de coup de soleil (pas un polluant atmosphérique). Pratique à côté de la planification qualité de l\'air en plein air par temps clair.',
        },
        aod: {
            title: 'AOD / visibilité',
            text: 'L\'Aerosol Optical Depth décrit à quel point l\'atmosphère est « brumeuse ». L\'app le convertit en visibilité approximative en km (clair → brume dense).',
        },
        pollen: {
            title: 'Pollen',
            text: 'Comptages de pollen pertinents pour les allergies (Europe). Niveaux par espèce ; le graphique suit le pic quotidien sur tous les types.',
        },
        disclaimer: 'Cette page est informative, pas un avis médical. Pour les décisions de santé, suivez les recommandations officielles locales sur la qualité de l\'air.',
    },
    settings: {
        intro: {
            title: 'Aperçu des réglages',
            text: 'Ouvrez les réglages de l\'app dans Garmin Connect IQ (téléphone) ou Garmin Express. Le standard AQI peut aussi être modifié depuis le menu sur la montre.',
        },
        aqStd: {
            title: 'Standard AQI',
            text: 'Européen ou US. Affecte le chiffre, les couleurs et les catégories partout (vue principale, graphiques, Glance, complications).',
        },
        pages: {
            title: 'Pages 1–6',
            text: 'Chaque emplacement peut être : Actuel, Prévision 24 h, Prévision 5 jours, Poussière, Particules, Brume/visibilité, Pollen – ou Aucune pour masquer l\'emplacement.',
            tip: 'Conseil : mettez Actuel en premier, puis les prévisions que vous utilisez le plus. Masquez les pages inutilisées pour moins balayer.',
        },
        series: {
            title: 'Série particules A / B',
            text: 'Choisissez quelles deux métriques le graphique de comparaison affiche (PM2.5, PM10, Poussière, UV, O₃, NO₂, CO, SO₂, AOD). Réglez une série sur « Aucune » pour la masquer ; une série utilise toute la largeur.',
        },
        location: {
            title: 'Localisation alternative',
            text: 'Latitude / longitude optionnelles. Laissez 0 / 0 pour utiliser la position de la montre. Définissez les deux pour épingler un lieu fixe (domicile, travail, vacances).',
        },
        interval: {
            title: 'Intervalle de mise à jour',
            text: 'Fréquence d\'actualisation du service en arrière-plan (minutes). Minimum 10, maximum 720. Défaut 60. Intervalles plus courts consomment plus de données et de batterie.',
        },
        comp: {
            title: 'Complications',
            forDevs: 'Pour les développeurs :',
            intro: 'Cette app publie des complications à utiliser dans les cadrans',
            aqi: '"AtmosAirAQI" : Indice de qualité de l’air actuel (indice UE ou US, selon le choix de l’utilisateur)',
            aqiRange: 'Inclut le « Range » indiquant où se situe cette valeur.',
            ts: '"AtmosAirTs" : Horodatage UTC de la valeur actuelle',
            longLabel: 'Long Label : « AtmosAirAQI »',
        },
        limits: {
            title: 'Limites appareil (faible mémoire)',
            warning: 'Sur certains modèles anciens / à faible mémoire (p. ex. Instinct 2, Forerunner 55, Fenix 6 et similaires), les pages supplémentaires Poussière, Particules, Brume et Pollen ne sont pas disponibles. Actuel + prévisions restent.',
            text: 'C\'est intentionnel pour que le widget reste stable dans les limites mémoire Garmin sur ces appareils.',
        },
        supportBefore: 'Questions ou problèmes ? Veuillez utiliser le',
        supportLink: 'formulaire de contact',
        supportAfter: '.',
    },
};
export const aqproEs = {
    views: {
        intro: 'Atmos Air Quality muestra la calidad del aire actual y las previsiones como widget de Garmin. Desliza o usa Arriba/Abajo para moverte entre las páginas activadas en los ajustes.',
        current: {
            title: 'Calidad del aire actual',
            gauge: 'Gran indicador de color: AQI actual (UE o US según tu ajuste). El marcador muestra tu posición en la escala.',
            category: 'Etiqueta de categoría (p. ej. Bueno / Moderado / Malo) debajo o junto al valor.',
            pollutants: 'Los valores PM2.5, PM10 y O₃ se muestran como referencia rápida.',
            location: 'Nombre del lugar y antigüedad de los datos.',
        },
        hourly: {
            title: 'Previsión de 24 horas',
            bars: 'Gráfico de barras del AQI para las próximas 24 horas, desde la hora actual.',
            colors: 'Los colores de las barras siguen las mismas categorías AQI que el indicador principal.',
            axis: 'El tiempo va de izquierda → derecha (ahora → más tarde).',
        },
        daily: {
            title: 'Previsión de 5 días',
            range: 'Cada día muestra el máximo y mínimo AQI esperados.',
            use: 'Útil para planificar actividades al aire libre en los próximos días.',
        },
        dust: {
            title: 'Polvo sahariano / polvo',
            current: 'Nivel de polvo actual más tendencia de 24 horas.',
            daily: 'Perspectiva de polvo de varios días cuando hay datos disponibles.',
            note: 'Especialmente relevante durante eventos de polvo sahariano que elevan los niveles de PM.',
        },
        particles: {
            title: 'Comparación de partículas',
            dual: 'Gráfico doble que compara dos series elegidas en los ajustes (p. ej. PM2.5 vs PM10, o O₃ vs NO₂).',
            single: 'Si solo se selecciona una serie, usa todo el ancho.',
            series: 'Series disponibles: PM2.5, PM10, Polvo, UV, O₃, NO₂, CO, SO₂, AOD.',
        },
        visibility: {
            title: 'Bruma / visibilidad (AOD)',
            aod: 'Basado en Aerosol Optical Depth (AOD) – cuánto oscurecen los aerosoles la atmósfera.',
            km: 'Convertido en visibilidad aproximada en kilómetros.',
            chart: 'Gráfico 24 h: barras más altas ≈ mejor visibilidad; el color indica la densidad de bruma.',
        },
        pollen: {
            title: 'Polen (Europa)',
            species: 'Seis tipos: aliso, abedul, gramíneas, artemisa, olivo, ambrosía – cada uno con barra de nivel.',
            chart: 'Gráfico inferior: máximo de 24 h entre todos los tipos.',
            europe: 'Los datos provienen de CAMS y suelen estar disponibles solo en Europa. Fuera de Europa puedes ver « Sin datos ».',
        },
        glance: {
            title: 'Vista Glance',
            what: 'Glance compacto en la esfera: valor AQI actual, categoría y escala de color con marcador.',
            mono: 'En relojes Instinct monocromos se usa una barra de progreso en lugar de la escala de color.',
        },
    },
    controls: {
        nav: {
            title: 'Navegar entre páginas',
            swipe: 'Pantalla táctil: desliza izquierda/derecha (o arriba/abajo según el reloj) para cambiar de página.',
            buttons: 'Relojes con botones: Arriba / Abajo o Página siguiente / anterior.',
            order: 'Solo aparecen las páginas asignadas en los ajustes (Página 1–6). Los huecos vacíos (« Ninguna ») se omiten.',
        },
        refresh: {
            title: 'Actualización manual',
            text: 'Pulsa Select / Enter / Start para solicitar datos nuevos al instante. El reloj necesita conexión con el teléfono (Bluetooth) para la descarga.',
            bg: 'En segundo plano la app también se actualiza automáticamente según el intervalo de los ajustes (60 minutos por defecto).',
        },
        menu: {
            title: 'Menú en el reloj (estándar AQI)',
            text: 'Abre el menú en el reloj para cambiar entre AQI europeo y AQI US. Escribe el mismo ajuste que en Connect IQ Mobile – prevalece el último cambio.',
        },
        location: {
            title: 'Ubicación',
            text: 'Por defecto la app usa la ubicación del reloj/teléfono. Puedes sustituirla con coordenadas alternativas en los ajustes (útil si quieres la calidad del aire de otro lugar).',
            gps: 'Si falta la ubicación o está desactualizada, obtén una señal GPS (inicia brevemente una actividad o usa el widget GPS) y actualiza.',
        },
        connection: {
            title: 'Conexión con el teléfono',
            text: 'Las peticiones a Internet pasan por el teléfono emparejado. Si ves un error de conexión, abre Garmin Connect, mantén el Bluetooth activo e intenta una actualización manual.',
        },
    },
    index: {
        intro: {
            title: 'Dos estándares – una app',
            text: 'Atmos Air Quality puede mostrar el índice europeo de calidad del aire (EAQI) o el AQI US (EPA). El número, la escala de color y las categorías cambian según el estándar – las mediciones de contaminantes subyacentes son las mismas.',
            switch: 'Cambia en los ajustes de Connect IQ (« Estándar AQI ») o mediante el menú en el reloj.',
        },
        diff: {
            title: 'Principales diferencias',
            scale: 'Escala: la UE suele usar valores absolutos más bajos (el indicador termina alrededor de 120). El AQI US a menudo llega a 300+ en el indicador.',
            thresholds: 'Los umbrales de categoría difieren – el mismo aire puede parecer « moderado » en un sistema y « insalubre para grupos sensibles » en el otro.',
            use: 'Usa UE si estás acostumbrado a apps/medios europeos; US si comparas con informes EPA / AirNow.',
        },
        table: { range: 'Índice', category: 'Categoría' },
        eu: {
            title: 'AQI europeo (EAQI)',
            text: 'Combina varios contaminantes en un índice. Categorías aproximadas: Bueno → Aceptable → Moderado → Malo → Muy malo → Extremadamente malo.',
            c0: 'Bueno',
            c1: 'Aceptable',
            c2: 'Moderado',
            c3: 'Malo',
            c4: 'Muy malo',
            c5: 'Extremadamente malo',
        },
        us: {
            title: 'AQI US (EPA)',
            text: 'Ampliamente usado en EE. UU. Categorías: Good → Moderate → Unhealthy for Sensitive Groups → Unhealthy → Very Unhealthy → Hazardous.',
            c0: 'Good',
            c1: 'Moderate',
            c2: 'Unhealthy for Sensitive Groups',
            c3: 'Unhealthy',
            c4: 'Very Unhealthy',
            c5: 'Hazardous',
        },
        warning: 'No mezcles los dos números. Un « 80 » en la escala UE no es lo mismo que un « 80 » en la escala US.',
    },
    values: {
        intro: {
            title: '¿Qué significan los valores?',
            text: 'El AQI es un índice resumen. Las páginas de contaminantes y el gráfico de partículas permiten ver qué componente está elevado. Las unidades de partículas y gases suelen ser µg/m³ salvo indicación contraria.',
        },
        pm25: {
            title: 'PM2.5',
            text: 'Partículas finas menores de 2,5 µm. Pueden penetrar profundamente en los pulmones. A menudo el principal factor de un mal AQI en ciudades y durante humo de leña / incendios.',
        },
        pm10: {
            title: 'PM10',
            text: 'Partículas más gruesas hasta 10 µm (polvo, fragmentos de polen, abrasión vial). Menos profundas que PM2.5, pero relevantes para irritación respiratoria.',
        },
        o3: {
            title: 'Ozono (O₃)',
            text: 'El ozono a nivel del suelo se forma a partir de gases de tráfico/industria con sol. Suele ser más alto por las tardes soleadas; puede irritar las vías respiratorias durante el deporte al aire libre.',
        },
        no2: {
            title: 'Dióxido de nitrógeno (NO₂)',
            text: 'Típicamente vinculado al tráfico y la combustión. Elevado cerca de carreteras concurridas; forma parte del cálculo del índice europeo.',
        },
        so2: {
            title: 'Dióxido de azufre (SO₂)',
            text: 'De la quema de combustibles con azufre y algunas fuentes industriales. Suele ser más bajo que PM o NO₂ en muchas ciudades de Europa occidental.',
        },
        co: {
            title: 'Monóxido de carbono (CO)',
            text: 'Combustión incompleta (tráfico, calefacción). Peligroso en altas concentraciones interiores; los niveles exteriores suelen ser mucho más bajos.',
        },
        dust: {
            title: 'Polvo',
            text: 'Carga de polvo mineral / sahariano. Los picos pueden elevar los valores PM en grandes regiones lejos de la fuente.',
        },
        uv: {
            title: 'Índice UV',
            text: 'Índice de riesgo de quemadura solar (no es un contaminante del aire). Útil junto a la planificación de calidad del aire al aire libre en días despejados.',
        },
        aod: {
            title: 'AOD / visibilidad',
            text: 'Aerosol Optical Depth describe cuán « brumosa » está la atmósfera. La app lo convierte en visibilidad aproximada en km (claro → bruma densa).',
        },
        pollen: {
            title: 'Polen',
            text: 'Recuentos de polen relevantes para alergias (Europa). Niveles por especie; el gráfico sigue el pico diario entre tipos.',
        },
        disclaimer: 'Esta página es informativa, no consejo médico. Para decisiones de salud sigue las recomendaciones oficiales locales sobre calidad del aire.',
    },
    settings: {
        intro: {
            title: 'Resumen de ajustes',
            text: 'Abre los ajustes de la app en Garmin Connect IQ (teléfono) o Garmin Express. El estándar AQI también puede cambiarse desde el menú en el reloj.',
        },
        aqStd: {
            title: 'Estándar AQI',
            text: 'Europeo o US. Afecta al número, colores y categorías en todas partes (vista principal, gráficos, Glance, complicaciones).',
        },
        pages: {
            title: 'Páginas 1–6',
            text: 'Cada ranura puede ser: Actual, Previsión 24 h, Previsión 5 días, Polvo, Partículas, Bruma/visibilidad, Polen – o Ninguna para ocultar la ranura.',
            tip: 'Consejo: pon Actual primero, luego las previsiones que más uses. Oculta páginas no usadas para deslizar menos.',
        },
        series: {
            title: 'Serie de partículas A / B',
            text: 'Elige qué dos métricas muestra el gráfico de comparación (PM2.5, PM10, Polvo, UV, O₃, NO₂, CO, SO₂, AOD). Pon una serie en « Ninguna » para ocultarla; una serie usa todo el ancho.',
        },
        location: {
            title: 'Ubicación alternativa',
            text: 'Latitud / longitud opcionales. Deja 0 / 0 para usar la ubicación del reloj. Establece ambas para fijar un lugar (casa, trabajo, vacaciones).',
        },
        interval: {
            title: 'Intervalo de actualización',
            text: 'Con qué frecuencia el servicio en segundo plano debe actualizar (minutos). Mínimo 10, máximo 720. Predeterminado 60. Intervalos más cortos usan más datos y batería.',
        },
        comp: {
            title: 'Complicaciones',
            forDevs: 'Para desarrolladores:',
            intro: 'Esta app publica complicaciones para usar en esferas',
            aqi: '"AtmosAirAQI": Índice de calidad del aire actual (índice UE o US, según la selección del usuario)',
            aqiRange: 'Incluye el “Range” que indica dónde cae este valor.',
            ts: '"AtmosAirTs": Marca de tiempo UTC del valor actual',
            longLabel: 'Long Label: “AtmosAirAQI”',
        },
        limits: {
            title: 'Límites del dispositivo (poca memoria)',
            warning: 'En algunos modelos antiguos / de poca memoria (p. ej. Instinct 2, Forerunner 55, Fenix 6 y similares) las páginas extra Polvo, Partículas, Bruma y Polen no están disponibles. Actual + previsiones permanecen.',
            text: 'Es intencional para que el widget se mantenga estable dentro de los límites de memoria de Garmin en esos dispositivos.',
        },
        supportBefore: '¿Preguntas o problemas? Usa el',
        supportLink: 'formulario de contacto',
        supportAfter: '.',
    },
};
export const aqproIt = {
    views: {
        intro: 'Atmos Air Quality mostra la qualità dell\'aria attuale e le previsioni come widget Garmin. Scorri o usa Su/Giù per spostarti tra le pagine attivate nelle impostazioni.',
        current: {
            title: 'Qualità dell\'aria attuale',
            gauge: 'Grande indicatore colorato: AQI attuale (UE o US in base all\'impostazione). Il marcatore mostra la posizione sulla scala.',
            category: 'Etichetta categoria (es. Buono / Moderato / Scarso) sotto o accanto al valore.',
            pollutants: 'I valori PM2.5, PM10 e O₃ sono mostrati come riferimento rapido.',
            location: 'Nome del luogo e quanto tempo fa sono stati aggiornati i dati.',
        },
        hourly: {
            title: 'Previsione 24 ore',
            bars: 'Grafico a barre dell\'AQI per le prossime 24 ore, a partire dall\'ora attuale.',
            colors: 'I colori delle barre seguono le stesse categorie AQI dell\'indicatore principale.',
            axis: 'Il tempo va da sinistra → destra (adesso → più tardi).',
        },
        daily: {
            title: 'Previsione 5 giorni',
            range: 'Ogni giorno mostra il massimo e il minimo AQI attesi.',
            use: 'Utile per pianificare attività all\'aperto nei prossimi giorni.',
        },
        dust: {
            title: 'Polvere sahariana / polvere',
            current: 'Livello di polvere attuale più andamento 24 ore.',
            daily: 'Prospettiva polvere su più giorni quando i dati sono disponibili.',
            note: 'Particolarmente rilevante durante eventi di polvere sahariana che aumentano i livelli di PM.',
        },
        particles: {
            title: 'Confronto particelle',
            dual: 'Grafico doppio che confronta due serie scelte nelle impostazioni (es. PM2.5 vs PM10, o O₃ vs NO₂).',
            single: 'Se è selezionata una sola serie, usa tutta la larghezza.',
            series: 'Serie disponibili: PM2.5, PM10, Polvere, UV, O₃, NO₂, CO, SO₂, AOD.',
        },
        visibility: {
            title: 'Foschia / visibilità (AOD)',
            aod: 'Basato su Aerosol Optical Depth (AOD) – quanto gli aerosol offuscano l\'atmosfera.',
            km: 'Convertito in visibilità approssimativa in chilometri.',
            chart: 'Grafico 24 h: barre più alte ≈ migliore visibilità; il colore indica la densità della foschia.',
        },
        pollen: {
            title: 'Polline (Europa)',
            species: 'Sei tipi: ontano, betulla, graminacee, assenzio, olivo, ambrosia – ciascuno con barra di livello.',
            chart: 'Grafico in basso: massimo 24 h su tutti i tipi.',
            europe: 'I dati provengono da CAMS e sono tipicamente disponibili solo in Europa. Fuori dall\'Europa potresti vedere « Nessun dato ».',
        },
        glance: {
            title: 'Vista Glance',
            what: 'Glance compatto sul quadrante: valore AQI attuale, categoria e scala colori con marcatore.',
            mono: 'Su orologi Instinct monocromatici viene usata una barra di progresso al posto della scala colori.',
        },
    },
    controls: {
        nav: {
            title: 'Navigare tra le pagine',
            swipe: 'Touchscreen: scorri sinistra/destra (o su/giù a seconda dell\'orologio) per cambiare pagina.',
            buttons: 'Orologi con pulsanti: Su / Giù o Pagina successiva / precedente.',
            order: 'Compaiono solo le pagine assegnate nelle impostazioni (Pagina 1–6). Gli slot vuoti (« Nessuna ») vengono saltati.',
        },
        refresh: {
            title: 'Aggiornamento manuale',
            text: 'Premi Select / Enter / Start per richiedere dati freschi subito. L\'orologio necessita di connessione al telefono (Bluetooth) per il download.',
            bg: 'In background l\'app si aggiorna anche automaticamente secondo l\'intervallo delle impostazioni (60 minuti predefiniti).',
        },
        menu: {
            title: 'Menu sull\'orologio (standard AQI)',
            text: 'Apri il menu sull\'orologio per passare tra AQI europeo e AQI US. Scrive la stessa impostazione di Connect IQ Mobile – prevale l\'ultima modifica.',
        },
        location: {
            title: 'Posizione',
            text: 'Per impostazione predefinita l\'app usa la posizione di orologio/telefono. Puoi sostituirla con coordinate alternative nelle impostazioni (utile per la qualità dell\'aria di un altro luogo).',
            gps: 'Se la posizione manca o è obsoleta, ottieni un fix GPS (avvia brevemente un\'attività o usa il widget GPS), poi aggiorna.',
        },
        connection: {
            title: 'Connessione telefono',
            text: 'Le richieste Internet passano attraverso il telefono accoppiato. Se vedi un errore di connessione, apri Garmin Connect, mantieni il Bluetooth attivo e prova un aggiornamento manuale.',
        },
    },
    index: {
        intro: {
            title: 'Due standard – un\'app',
            text: 'Atmos Air Quality può mostrare l\'indice europeo della qualità dell\'aria (EAQI) o l\'AQI US (EPA). Numero, scala colori e categorie cambiano con lo standard – le misure dei inquinanti sottostanti restano le stesse.',
            switch: 'Cambia nelle impostazioni Connect IQ (« Standard AQI ») o tramite il menu sull\'orologio.',
        },
        diff: {
            title: 'Principali differenze',
            scale: 'Scala: l\'UE usa tipicamente valori assoluti più bassi (l\'indicatore termina intorno a 120). L\'AQI US arriva spesso fino a 300+ sull\'indicatore.',
            thresholds: 'Le soglie di categoria differiscono – la stessa aria può sembrare « moderata » in un sistema e « malsana per gruppi sensibili » nell\'altro.',
            use: 'Usa UE se sei abituato ad app/media europee; US se confronti con report EPA / AirNow.',
        },
        table: { range: 'Indice', category: 'Categoria' },
        eu: {
            title: 'AQI europeo (EAQI)',
            text: 'Combina diversi inquinanti in un indice. Categorie approssimative: Buono → Discreto → Moderato → Scarso → Molto scarso → Estremamente scarso.',
            c0: 'Buono',
            c1: 'Discreto',
            c2: 'Moderato',
            c3: 'Scarso',
            c4: 'Molto scarso',
            c5: 'Estremamente scarso',
        },
        us: {
            title: 'AQI US (EPA)',
            text: 'Ampiamente usato negli USA. Categorie: Good → Moderate → Unhealthy for Sensitive Groups → Unhealthy → Very Unhealthy → Hazardous.',
            c0: 'Good',
            c1: 'Moderate',
            c2: 'Unhealthy for Sensitive Groups',
            c3: 'Unhealthy',
            c4: 'Very Unhealthy',
            c5: 'Hazardous',
        },
        warning: 'Non mescolare i due numeri. Un « 80 » sulla scala UE non è uguale a un « 80 » sulla scala US.',
    },
    values: {
        intro: {
            title: 'Cosa significano i valori?',
            text: 'L\'AQI è un indice di sintesi. Le pagine inquinanti e il grafico particelle mostrano quale componente è elevata. Le unità per particelle e gas sono tipicamente µg/m³ salvo diversa indicazione.',
        },
        pm25: {
            title: 'PM2.5',
            text: 'Particelle fini inferiori a 2,5 µm. Possono penetrare in profondità nei polmoni. Spesso il fattore principale di un AQI scarso in città e durante fumo di legna / incendi.',
        },
        pm10: {
            title: 'PM10',
            text: 'Particelle più grossolane fino a 10 µm (polvere, frammenti di polline, abrasione stradale). Meno profonde di PM2.5, ma rilevanti per irritazione respiratoria.',
        },
        o3: {
            title: 'Ozono (O₃)',
            text: 'L\'ozono a livello del suolo si forma da gas di traffico/industria con il sole. Spesso più alto nei pomeriggi soleggiati; può irritare le vie respiratorie durante lo sport all\'aperto.',
        },
        no2: {
            title: 'Biossido di azoto (NO₂)',
            text: 'Tipicamente legato a traffico e combustione. Elevato vicino a strade trafficate; parte del calcolo dell\'indice europeo.',
        },
        so2: {
            title: 'Biossido di zolfo (SO₂)',
            text: 'Dalla combustione di combustibili contenenti zolfo e alcune fonti industriali. Di solito più basso di PM o NO₂ in molte città dell\'Europa occidentale.',
        },
        co: {
            title: 'Monossido di carbonio (CO)',
            text: 'Combustione incompleta (traffico, riscaldamento). Pericoloso in alte concentrazioni indoor; i livelli outdoor sono di solito molto più bassi.',
        },
        dust: {
            title: 'Polvere',
            text: 'Carico di polvere minerale / sahariana. I picchi possono aumentare i valori PM su vaste regioni lontano dalla fonte.',
        },
        uv: {
            title: 'Indice UV',
            text: 'Indice di rischio scottature (non un inquinante dell\'aria). Utile accanto alla pianificazione della qualità dell\'aria all\'aperto nelle giornate limpide.',
        },
        aod: {
            title: 'AOD / visibilità',
            text: 'Aerosol Optical Depth descrive quanto l\'atmosfera è « fosca ». L\'app lo converte in visibilità approssimativa in km (chiaro → foschia densa).',
        },
        pollen: {
            title: 'Polline',
            text: 'Conteggi di polline rilevanti per le allergie (Europa). Livelli per specie; il grafico segue il picco giornaliero tra i tipi.',
        },
        disclaimer: 'Questa pagina è informativa, non un consiglio medico. Per decisioni sulla salute segui le indicazioni ufficiali locali sulla qualità dell\'aria.',
    },
    settings: {
        intro: {
            title: 'Panoramica impostazioni',
            text: 'Apri le impostazioni dell\'app in Garmin Connect IQ (telefono) o Garmin Express. Lo standard AQI può essere cambiato anche dal menu sull\'orologio.',
        },
        aqStd: {
            title: 'Standard AQI',
            text: 'Europeo o US. Influenza numero, colori e categorie ovunque (vista principale, grafici, Glance, complicazioni).',
        },
        pages: {
            title: 'Pagine 1–6',
            text: 'Ogni slot può essere: Attuale, Previsione 24 h, Previsione 5 giorni, Polvere, Particelle, Foschia/visibilità, Polline – o Nessuna per nascondere lo slot.',
            tip: 'Suggerimento: metti Attuale per prima, poi le previsioni che usi di più. Nascondi le pagine inutilizzate per scorrere meno.',
        },
        series: {
            title: 'Serie particelle A / B',
            text: 'Scegli quali due metriche mostra il grafico di confronto (PM2.5, PM10, Polvere, UV, O₃, NO₂, CO, SO₂, AOD). Imposta una serie su « Nessuna » per nasconderla; una serie usa tutta la larghezza.',
        },
        location: {
            title: 'Posizione alternativa',
            text: 'Latitudine / longitudine opzionali. Lascia 0 / 0 per usare la posizione dell\'orologio. Imposta entrambe per fissare un luogo (casa, lavoro, vacanza).',
        },
        interval: {
            title: 'Intervallo di aggiornamento',
            text: 'Con quale frequenza il servizio in background deve aggiornare (minuti). Minimo 10, massimo 720. Predefinito 60. Intervalli più brevi usano più dati e batteria.',
        },
        comp: {
            title: 'Complication',
            forDevs: 'Per gli sviluppatori:',
            intro: 'Questa app pubblica complication da usare nei quadranti',
            aqi: '"AtmosAirAQI": Indice di qualità dell’aria attuale (indice UE o US, a seconda della selezione dell’utente)',
            aqiRange: 'Include il “Range” che indica dove cade questo valore.',
            ts: '"AtmosAirTs": Timestamp UTC del valore attuale',
            longLabel: 'Long Label: “AtmosAirAQI”',
        },
        limits: {
            title: 'Limiti dispositivo (poca memoria)',
            warning: 'Su alcuni modelli più vecchi / a bassa memoria (es. Instinct 2, Forerunner 55, Fenix 6 e simili) le pagine extra Polvere, Particelle, Foschia e Polline non sono disponibili. Attuale + previsioni restano.',
            text: 'È intenzionale per mantenere il widget stabile entro i limiti di memoria Garmin su quei dispositivi.',
        },
        supportBefore: 'Domande o problemi? Usa il',
        supportLink: 'modulo di contatto',
        supportAfter: '.',
    },
};
export const aqproPt = {
    views: {
        intro: 'Atmos Air Quality mostra a qualidade do ar atual e previsões como widget Garmin. Deslize ou use Cima/Baixo para alternar entre as páginas ativadas nas definições.',
        current: {
            title: 'Qualidade do ar atual',
            gauge: 'Grande indicador colorido: AQI atual (UE ou US conforme a definição). O marcador mostra a posição na escala.',
            category: 'Etiqueta de categoria (ex. Bom / Moderado / Mau) abaixo ou junto ao valor.',
            pollutants: 'Os valores PM2.5, PM10 e O₃ são mostrados como referência rápida.',
            location: 'Nome do local e há quanto tempo os dados foram atualizados.',
        },
        hourly: {
            title: 'Previsão de 24 horas',
            bars: 'Gráfico de barras do AQI para as próximas 24 horas, a partir da hora atual.',
            colors: 'As cores das barras seguem as mesmas categorias AQI do indicador principal.',
            axis: 'O tempo vai da esquerda → direita (agora → mais tarde).',
        },
        daily: {
            title: 'Previsão de 5 dias',
            range: 'Cada dia mostra o máximo e mínimo AQI esperados.',
            use: 'Útil para planear atividades ao ar livre nos próximos dias.',
        },
        dust: {
            title: 'Poeira saariana / poeira',
            current: 'Nível de poeira atual mais tendência de 24 horas.',
            daily: 'Perspetiva de poeira de vários dias quando há dados disponíveis.',
            note: 'Especialmente relevante durante eventos de poeira saariana que elevam os níveis de PM.',
        },
        particles: {
            title: 'Comparação de partículas',
            dual: 'Gráfico duplo comparando duas séries escolhidas nas definições (ex. PM2.5 vs PM10, ou O₃ vs NO₂).',
            single: 'Se apenas uma série for selecionada, usa toda a largura.',
            series: 'Séries disponíveis: PM2.5, PM10, Poeira, UV, O₃, NO₂, CO, SO₂, AOD.',
        },
        visibility: {
            title: 'Nevoeiro / visibilidade (AOD)',
            aod: 'Baseado em Aerosol Optical Depth (AOD) – quanto os aerosóis escurecem a atmosfera.',
            km: 'Convertido em visibilidade aproximada em quilómetros.',
            chart: 'Gráfico 24 h: barras mais altas ≈ melhor visibilidade; a cor indica a densidade do nevoeiro.',
        },
        pollen: {
            title: 'Pólen (Europa)',
            species: 'Seis tipos: amieiro, bétula, gramíneas, artemísia, oliveira, ambrosia – cada um com barra de nível.',
            chart: 'Gráfico inferior: máximo de 24 h entre todos os tipos.',
            europe: 'Os dados vêm do CAMS e estão tipicamente disponíveis apenas na Europa. Fora da Europa pode ver « Sem dados ».',
        },
        glance: {
            title: 'Vista Glance',
            what: 'Glance compacto no mostrador: valor AQI atual, categoria e escala de cores com marcador.',
            mono: 'Em relógios Instinct monocromáticos usa-se uma barra de progresso em vez da escala de cores.',
        },
    },
    controls: {
        nav: {
            title: 'Navegar entre páginas',
            swipe: 'Ecrã tátil: deslize esquerda/direita (ou cima/baixo conforme o relógio) para mudar de página.',
            buttons: 'Relógios com botões: Cima / Baixo ou Página seguinte / anterior.',
            order: 'Só aparecem as páginas atribuídas nas definições (Página 1–6). Os slots vazios (« Nenhuma ») são ignorados.',
        },
        refresh: {
            title: 'Atualização manual',
            text: 'Prima Select / Enter / Start para pedir dados novos de imediato. O relógio precisa de ligação ao telefone (Bluetooth) para a transferência.',
            bg: 'Em segundo plano a app também atualiza automaticamente conforme o intervalo das definições (60 minutos por defeito).',
        },
        menu: {
            title: 'Menu no relógio (padrão AQI)',
            text: 'Abra o menu no relógio para alternar entre AQI europeu e AQI US. Escreve a mesma definição que no Connect IQ Mobile – prevalece a última alteração.',
        },
        location: {
            title: 'Localização',
            text: 'Por defeito a app usa a localização do relógio/telefone. Pode substituí-la por coordenadas alternativas nas definições (útil para qualidade do ar noutro local).',
            gps: 'Se a localização faltar ou estiver desatualizada, obtenha fix GPS (inicie brevemente uma atividade ou use o widget GPS) e atualize.',
        },
        connection: {
            title: 'Ligação ao telefone',
            text: 'Os pedidos à Internet passam pelo telefone emparelhado. Se vir erro de ligação, abra Garmin Connect, mantenha o Bluetooth ativo e tente uma atualização manual.',
        },
    },
    index: {
        intro: {
            title: 'Dois padrões – uma app',
            text: 'Atmos Air Quality pode mostrar o índice europeu de qualidade do ar (EAQI) ou o AQI US (EPA). O número, a escala de cores e as categorias mudam com o padrão – as medições de poluentes subjacentes permanecem iguais.',
            switch: 'Alterne nas definições Connect IQ (« Padrão AQI ») ou pelo menu no relógio.',
        },
        diff: {
            title: 'Principais diferenças',
            scale: 'Escala: a UE usa tipicamente valores absolutos mais baixos (o indicador termina cerca de 120). O AQI US sobe frequentemente até 300+ no indicador.',
            thresholds: 'Os limiares de categoria diferem – o mesmo ar pode parecer « moderado » num sistema e « insalubre para grupos sensíveis » noutro.',
            use: 'Use UE se está habituado a apps/media europeus; US se compara com relatórios EPA / AirNow.',
        },
        table: { range: 'Índice', category: 'Categoria' },
        eu: {
            title: 'AQI europeu (EAQI)',
            text: 'Combina vários poluentes num índice. Categorias aproximadas: Bom → Razoável → Moderado → Mau → Muito mau → Extremamente mau.',
            c0: 'Bom',
            c1: 'Razoável',
            c2: 'Moderado',
            c3: 'Mau',
            c4: 'Muito mau',
            c5: 'Extremamente mau',
        },
        us: {
            title: 'AQI US (EPA)',
            text: 'Amplamente usado nos EUA. Categorias: Good → Moderate → Unhealthy for Sensitive Groups → Unhealthy → Very Unhealthy → Hazardous.',
            c0: 'Good',
            c1: 'Moderate',
            c2: 'Unhealthy for Sensitive Groups',
            c3: 'Unhealthy',
            c4: 'Very Unhealthy',
            c5: 'Hazardous',
        },
        warning: 'Não misture os dois números. Um « 80 » na escala UE não é o mesmo que um « 80 » na escala US.',
    },
    values: {
        intro: {
            title: 'O que significam os valores?',
            text: 'O AQI é um índice resumo. As páginas de poluentes e o gráfico de partículas mostram qual componente está elevada. Unidades de partículas e gases são tipicamente µg/m³ salvo indicação em contrário.',
        },
        pm25: {
            title: 'PM2.5',
            text: 'Partículas finas menores que 2,5 µm. Penetram profundamente nos pulmões. Frequentemente o principal fator de um mau AQI em cidades e durante fumo de lenha / incêndios.',
        },
        pm10: {
            title: 'PM10',
            text: 'Partículas mais grossas até 10 µm (poeira, fragmentos de pólen, abrasão rodoviária). Menos profundas que PM2.5, mas relevantes para irritação respiratória.',
        },
        o3: {
            title: 'Ozono (O₃)',
            text: 'O ozono ao nível do solo forma-se a partir de gases de tráfego/indústria com sol. Frequentemente mais alto à tarde ensolarada; pode irritar vias respiratórias durante desporto ao ar livre.',
        },
        no2: {
            title: 'Dióxido de azoto (NO₂)',
            text: 'Tipicamente ligado a tráfego e combustão. Elevado junto a estradas movimentadas; parte do cálculo do índice europeu.',
        },
        so2: {
            title: 'Dióxido de enxofre (SO₂)',
            text: 'Da queima de combustíveis com enxofre e algumas fontes industriais. Geralmente mais baixo que PM ou NO₂ em muitas cidades da Europa ocidental.',
        },
        co: {
            title: 'Monóxido de carbono (CO)',
            text: 'Combustão incompleta (tráfego, aquecimento). Perigoso em altas concentrações interiores; níveis exteriores são normalmente muito mais baixos.',
        },
        dust: {
            title: 'Poeira',
            text: 'Carga de poeira mineral / saariana. Picos podem elevar valores PM em grandes regiões longe da fonte.',
        },
        uv: {
            title: 'Índice UV',
            text: 'Índice de risco de queimadura solar (não é um poluente do ar). Útil junto ao planeamento de qualidade do ar ao ar livre em dias limpos.',
        },
        aod: {
            title: 'AOD / visibilidade',
            text: 'Aerosol Optical Depth descreve quão « nebulosa » está a atmosfera. A app converte em visibilidade aproximada em km (claro → nevoeiro denso).',
        },
        pollen: {
            title: 'Pólen',
            text: 'Contagens de pólen relevantes para alergias (Europa). Níveis por espécie; o gráfico segue o pico diário entre tipos.',
        },
        disclaimer: 'Esta página é informativa, não aconselhamento médico. Para decisões de saúde siga as orientações oficiais locais sobre qualidade do ar.',
    },
    settings: {
        intro: {
            title: 'Visão geral das definições',
            text: 'Abra as definições da app no Garmin Connect IQ (telefone) ou Garmin Express. O padrão AQI também pode ser alterado pelo menu no relógio.',
        },
        aqStd: {
            title: 'Padrão AQI',
            text: 'Europeu ou US. Afeta número, cores e categorias em todo o lado (vista principal, gráficos, Glance, complicações).',
        },
        pages: {
            title: 'Páginas 1–6',
            text: 'Cada slot pode ser: Atual, Previsão 24 h, Previsão 5 dias, Poeira, Partículas, Nevoeiro/visibilidade, Pólen – ou Nenhuma para ocultar o slot.',
            tip: 'Dica: coloque Atual primeiro, depois as previsões que mais usa. Oculte páginas não usadas para deslizar menos.',
        },
        series: {
            title: 'Série de partículas A / B',
            text: 'Escolha quais duas métricas o gráfico de comparação mostra (PM2.5, PM10, Poeira, UV, O₃, NO₂, CO, SO₂, AOD). Defina uma série como « Nenhuma » para ocultar; uma série usa toda a largura.',
        },
        location: {
            title: 'Localização alternativa',
            text: 'Latitude / longitude opcionais. Deixe 0 / 0 para usar a localização do relógio. Defina ambas para fixar um local (casa, trabalho, férias).',
        },
        interval: {
            title: 'Intervalo de atualização',
            text: 'Com que frequência o serviço em segundo plano deve atualizar (minutos). Mínimo 10, máximo 720. Predefinição 60. Intervalos mais curtos usam mais dados e bateria.',
        },
        comp: {
            title: 'Complicações',
            forDevs: 'Para programadores:',
            intro: 'Esta app publica complicações para utilização em mostradores',
            aqi: '"AtmosAirAQI": Índice de qualidade do ar atual (índice UE ou US, consoante a seleção do utilizador)',
            aqiRange: 'Inclui o “Range” que indica onde este valor se situa.',
            ts: '"AtmosAirTs": Carimbo de data/hora UTC do valor atual',
            longLabel: 'Long Label: “AtmosAirAQI”',
        },
        limits: {
            title: 'Limites do dispositivo (pouca memória)',
            warning: 'Em alguns modelos mais antigos / com pouca memória (ex. Instinct 2, Forerunner 55, Fenix 6 e similares) as páginas extra Poeira, Partículas, Nevoeiro e Pólen não estão disponíveis. Atual + previsões permanecem.',
            text: 'É intencional para manter o widget estável dentro dos limites de memória Garmin nesses dispositivos.',
        },
        supportBefore: 'Questões ou problemas? Use o',
        supportLink: 'formulário de contacto',
        supportAfter: '.',
    },
};
export const aqproNl = {
    views: {
        intro: 'Atmos Air Quality toont actuele luchtkwaliteit en voorspellingen als Garmin-widget. Veeg of gebruik Omhoog/Omlaag om tussen de pagina\'s te wisselen die je in de instellingen hebt ingeschakeld.',
        current: {
            title: 'Actuele luchtkwaliteit',
            gauge: 'Grote gekleurde meter: huidige AQI (EU of US, afhankelijk van je instelling). De markering toont je positie op de schaal.',
            category: 'Categorielabel (bijv. Goed / Matig / Slecht) onder of naast de waarde.',
            pollutants: 'PM2.5-, PM10- en O₃-waarden worden als snelle referentie getoond.',
            location: 'Locatienaam en hoe lang geleden de gegevens zijn bijgewerkt.',
        },
        hourly: {
            title: '24-uursvoorspelling',
            bars: 'Staafdiagram van AQI voor de komende 24 uur, vanaf het huidige uur.',
            colors: 'Staafkleuren volgen dezelfde AQI-categorieën als de hoofdmeter.',
            axis: 'Tijd loopt links → rechts (nu → later).',
        },
        daily: {
            title: '5-daagse voorspelling',
            range: 'Elke dag toont het verwachte AQI-maximum en -minimum.',
            use: 'Handig voor het plannen van buitenactiviteiten in de komende dagen.',
        },
        dust: {
            title: 'Sahara-stof / stof',
            current: 'Huidig stofniveau plus 24-uurstrend.',
            daily: 'Meerdagse stofvooruitzichten wanneer gegevens beschikbaar zijn.',
            note: 'Vooral relevant tijdens Sahara-stofgebeurtenissen die PM-niveaus verhogen.',
        },
        particles: {
            title: 'Deeltjesvergelijking',
            dual: 'Dubbel diagram vergelijkt twee series uit de instellingen (bijv. PM2.5 vs PM10, of O₃ vs NO₂).',
            single: 'Als slechts één serie is geselecteerd, gebruikt deze de volledige breedte.',
            series: 'Beschikbare series: PM2.5, PM10, Stof, UV, O₃, NO₂, CO, SO₂, AOD.',
        },
        visibility: {
            title: 'Nevel / zicht (AOD)',
            aod: 'Gebaseerd op Aerosol Optical Depth (AOD) – hoeveel aerosolen de atmosfeer troebelen.',
            km: 'Omgezet naar geschat zicht in kilometers.',
            chart: '24-uursdiagram: hogere staven ≈ beter zicht; kleur geeft neveldichtheid aan.',
        },
        pollen: {
            title: 'Pollen (Europa)',
            species: 'Zes soorten: els, berk, gras, bijvoet, olijf, ambrosia – elk met een niveaubalk.',
            chart: 'Onderste diagram: 24-uursmaximum over alle soorten.',
            europe: 'Gegevens komen van CAMS en zijn doorgaans alleen in Europa beschikbaar. Buiten Europa zie je mogelijk « Geen gegevens ».',
        },
        glance: {
            title: 'Glance-weergave',
            what: 'Compacte Glance op de wijzerplaat: huidige AQI-waarde, categorie en kleurschaal met markering.',
            mono: 'Op monochroom Instinct-horloges wordt een voortgangsbalk gebruikt in plaats van de kleurschaal.',
        },
    },
    controls: {
        nav: {
            title: 'Navigeren tussen pagina\'s',
            swipe: 'Touchscreen: veeg links/rechts (of omhoog/omlaag, afhankelijk van het horloge) om van pagina te wisselen.',
            buttons: 'Knoppenhorloges: Omhoog / Omlaag of Volgende / Vorige pagina.',
            order: 'Alleen pagina\'s die je in de instellingen hebt toegewezen (Pagina 1–6) verschijnen. Lege slots (« Geen ») worden overgeslagen.',
        },
        refresh: {
            title: 'Handmatig vernieuwen',
            text: 'Druk op Select / Enter / Start om direct nieuwe gegevens op te vragen. Het horloge heeft een telefoonverbinding (Bluetooth) nodig voor de download.',
            bg: 'Op de achtergrond werkt de app ook automatisch bij volgens het interval uit de instellingen (standaard 60 minuten).',
        },
        menu: {
            title: 'Menu op het horloge (AQI-standaard)',
            text: 'Open het menu op het horloge om te wisselen tussen Europese AQI en US AQI. Dit schrijft dezelfde instelling als in Connect IQ Mobile – de laatste wijziging geldt.',
        },
        location: {
            title: 'Locatie',
            text: 'Standaard gebruikt de app de locatie van horloge/telefoon. Je kunt deze overschrijven met alternatieve coördinaten in de instellingen (handig voor luchtkwaliteit op een andere plek).',
            gps: 'Als de locatie ontbreekt of verouderd is: GPS-fix verkrijgen (start kort een activiteit of gebruik de GPS-widget), daarna vernieuwen.',
        },
        connection: {
            title: 'Telefoonverbinding',
            text: 'Internetverzoeken gaan via de gekoppelde telefoon. Bij een verbindingsfout: open Garmin Connect, houd Bluetooth aan en probeer handmatig te vernieuwen.',
        },
    },
    index: {
        intro: {
            title: 'Twee standaarden – één app',
            text: 'Atmos Air Quality kan de Europese luchtkwaliteitsindex (EAQI) of de US AQI (EPA) tonen. Het getal, de kleurschaal en categorielabels wijzigen met de standaard – de onderliggende vervuilingsmetingen blijven hetzelfde.',
            switch: 'Wissel in Connect IQ-instellingen (« AQI-standaard ») of via het menu op het horloge.',
        },
        diff: {
            title: 'Belangrijkste verschillen',
            scale: 'Schaal: EU werkt doorgaans met lagere absolute waarden (meter eindigt rond 120). US AQI gaat op de meter vaak tot 300+.',
            thresholds: 'Categoriedrempels verschillen – dezelfde lucht kan in het ene systeem « matig » en in het andere « ongezond voor gevoelige groepen » lijken.',
            use: 'Gebruik EU als je gewend bent aan Europese apps/media; US als je vergelijkt met EPA / AirNow-rapporten.',
        },
        table: { range: 'Index', category: 'Categorie' },
        eu: {
            title: 'Europese AQI (EAQI)',
            text: 'Combineert meerdere vervuilingsstoffen in één index. Categorieën globaal: Goed → Redelijk → Matig → Slecht → Zeer slecht → Extreem slecht.',
            c0: 'Goed',
            c1: 'Redelijk',
            c2: 'Matig',
            c3: 'Slecht',
            c4: 'Zeer slecht',
            c5: 'Extreem slecht',
        },
        us: {
            title: 'US AQI (EPA)',
            text: 'Veel gebruikt in de VS. Categorieën: Good → Moderate → Unhealthy for Sensitive Groups → Unhealthy → Very Unhealthy → Hazardous.',
            c0: 'Good',
            c1: 'Moderate',
            c2: 'Unhealthy for Sensitive Groups',
            c3: 'Unhealthy',
            c4: 'Very Unhealthy',
            c5: 'Hazardous',
        },
        warning: 'Meng de twee getallen niet. Een « 80 » op de EU-schaal is niet hetzelfde als een « 80 » op de US-schaal.',
    },
    values: {
        intro: {
            title: 'Wat betekenen de waarden?',
            text: 'De AQI is een samenvattende index. De vervuilingspagina\'s en het deeltjesdiagram laten zien welke component verhoogd is. Eenheden voor deeltjes en gassen zijn doorgaans µg/m³ tenzij anders vermeld.',
        },
        pm25: {
            title: 'PM2.5',
            text: 'Fijne deeltjes kleiner dan 2,5 µm. Ze dringen diep de longen in. Vaak de belangrijkste factor van een slechte AQI in steden en bij houtrook / bosbranden.',
        },
        pm10: {
            title: 'PM10',
            text: 'Grovere deeltjes tot 10 µm (stof, pollenfragmenten, wegslijtage). Minder diep dan PM2.5, maar relevant voor luchtwegirritatie.',
        },
        o3: {
            title: 'Ozon (O₃)',
            text: 'Ozon op grondniveau ontstaat uit verkeers-/industriegassen in zonlicht. Vaak hoger op zonnige middagen; kan luchtwegen irriteren tijdens buitensport.',
        },
        no2: {
            title: 'Stikstofdioxide (NO₂)',
            text: 'Typisch gekoppeld aan verkeer en verbranding. Verhoogd bij drukke wegen; onderdeel van de Europese indexberekening.',
        },
        so2: {
            title: 'Zwaveldioxide (SO₂)',
            text: 'Uit verbranding van zwavelhoudende brandstoffen en sommige industriële bronnen. Meestal lager dan PM of NO₂ in veel West-Europese steden.',
        },
        co: {
            title: 'Koolmonoxide (CO)',
            text: 'Onvolledige verbranding (verkeer, verwarming). Gevaarlijk bij hoge binnenconcentraties; buitenlevels zijn meestal veel lager.',
        },
        dust: {
            title: 'Stof',
            text: 'Minerale / Sahara-stofbelasting. Pieken kunnen PM-waarden in grote regio\'s verhogen, ver van de bron.',
        },
        uv: {
            title: 'UV-index',
            text: 'Zonnebrandrisico-index (geen luchtvervuiler). Handig naast buitenplanning van luchtkwaliteit op heldere dagen.',
        },
        aod: {
            title: 'AOD / zicht',
            text: 'Aerosol Optical Depth beschrijft hoe « nevelig » de atmosfeer is. De app zet dit om naar geschat zicht in km (helder → dichte nevel).',
        },
        pollen: {
            title: 'Pollen',
            text: 'Allergie-relevante pollentellingen (Europa). Niveaus per soort; het diagram volgt het dagelijkse piek over alle soorten.',
        },
        disclaimer: 'Deze pagina is informatief, geen medisch advies. Volg voor gezondheidsbeslissingen de officiële lokale luchtkwaliteitsrichtlijnen.',
    },
    settings: {
        intro: {
            title: 'Instellingenoverzicht',
            text: 'Open de app-instellingen in Garmin Connect IQ (telefoon) of Garmin Express. De AQI-standaard kan ook worden gewijzigd via het menu op het horloge.',
        },
        aqStd: {
            title: 'AQI-standaard',
            text: 'Europees of US. Beïnvloedt getal, kleuren en categorieën overal (hoofdweergave, diagrammen, Glance, complicaties).',
        },
        pages: {
            title: 'Pagina\'s 1–6',
            text: 'Elke slot kan zijn: Actueel, 24u-voorspelling, 5-daagse voorspelling, Stof, Deeltjes, Nevel/zicht, Pollen – of Geen om de slot te verbergen.',
            tip: 'Tip: zet Actueel eerst, daarna de voorspellingen die je het meest gebruikt. Verberg ongebruikte pagina\'s om minder te vegen.',
        },
        series: {
            title: 'Deeltjesserie A / B',
            text: 'Kies welke twee metingen het vergelijkingsdiagram toont (PM2.5, PM10, Stof, UV, O₃, NO₂, CO, SO₂, AOD). Zet een serie op « Geen » om te verbergen; één serie gebruikt de volledige breedte.',
        },
        location: {
            title: 'Alternatieve locatie',
            text: 'Optionele breedtegraad / lengtegraad. Laat 0 / 0 staan voor horlogelocatie. Stel beide in om een vaste plek vast te pinnen (thuis, werk, vakantie).',
        },
        interval: {
            title: 'Update-interval',
            text: 'Hoe vaak de achtergrondservice moet vernieuwen (minuten). Minimum 10, maximum 720. Standaard 60. Kortere intervallen gebruiken meer data en batterij.',
        },
        comp: {
            title: 'Complicaties',
            forDevs: 'Voor ontwikkelaars:',
            intro: 'Deze app publiceert complicaties voor gebruik in wijzerplaten',
            aqi: '"AtmosAirAQI": Huidige luchtkwaliteitsindex (EU- of US-index, afhankelijk van de keuze van de gebruiker)',
            aqiRange: 'Inclusief de “Range” die aangeeft waar deze waarde valt.',
            ts: '"AtmosAirTs": UTC-tijdstempel van de huidige waarde',
            longLabel: 'Long Label: “AtmosAirAQI”',
        },
        limits: {
            title: 'Apparaatlimieten (weinig geheugen)',
            warning: 'Op sommige oudere / geheugenarme modellen (bijv. Instinct 2, Forerunner 55, Fenix 6 en vergelijkbaar) zijn de extra pagina\'s Stof, Deeltjes, Nevel en Pollen niet beschikbaar. Actueel + voorspellingen blijven.',
            text: 'Dit is opzettelijk zodat de widget stabiel blijft binnen Garmin\'s geheugenlimieten op die apparaten.',
        },
        supportBefore: 'Vragen of problemen? Gebruik het',
        supportLink: 'contactformulier',
        supportAfter: '.',
    },
};
export const aqproPl = {
    views: {
        intro: 'Atmos Air Quality pokazuje aktualną jakość powietrza i prognozy jako widget Garmin. Przesuwaj lub używaj Góra/Dół, aby przełączać strony włączone w ustawieniach.',
        current: {
            title: 'Aktualna jakość powietrza',
            gauge: 'Duży kolorowy wskaźnik: bieżący AQI (UE lub US w zależności od ustawienia). Znacznik pokazuje pozycję na skali.',
            category: 'Etykieta kategorii (np. Dobra / Umiarkowana / Zła) pod lub obok wartości.',
            pollutants: 'Wartości PM2.5, PM10 i O₃ są pokazane jako szybkie odniesienie.',
            location: 'Nazwa lokalizacji i jak dawno zaktualizowano dane.',
        },
        hourly: {
            title: 'Prognoza 24-godzinna',
            bars: 'Wykres słupkowy AQI na następne 24 godziny, od bieżącej godziny.',
            colors: 'Kolory słupków odpowiadają tym samym kategoriom AQI co główny wskaźnik.',
            axis: 'Czas biegnie od lewej → prawej (teraz → później).',
        },
        daily: {
            title: 'Prognoza 5-dniowa',
            range: 'Każdy dzień pokazuje oczekiwane maksimum i minimum AQI.',
            use: 'Przydatne do planowania aktywności na świeżym powietrzu w nadchodzących dniach.',
        },
        dust: {
            title: 'Pył saharyjski / pył',
            current: 'Aktualny poziom pyłu plus 24-godzinny trend.',
            daily: 'Wielodniowa prognoza pyłu, gdy dane są dostępne.',
            note: 'Szczególnie istotne podczas zdarzeń pyłu saharyjskiego podnoszących poziomy PM.',
        },
        particles: {
            title: 'Porównanie cząstek',
            dual: 'Podwójny wykres porównujący dwie serie wybrane w ustawieniach (np. PM2.5 vs PM10 lub O₃ vs NO₂).',
            single: 'Jeśli wybrano tylko jedną serię, wykorzystuje pełną szerokość.',
            series: 'Dostępne serie: PM2.5, PM10, Pył, UV, O₃, NO₂, CO, SO₂, AOD.',
        },
        visibility: {
            title: 'Mgła / widoczność (AOD)',
            aod: 'Na podstawie Aerosol Optical Depth (AOD) – jak bardzo aerozole przyciemniają atmosferę.',
            km: 'Przeliczone na przybliżoną widoczność w kilometrach.',
            chart: 'Wykres 24 h: wyższe słupki ≈ lepsza widoczność; kolor wskazuje gęstość mgły.',
        },
        pollen: {
            title: 'Pyłki (Europa)',
            species: 'Sześć typów: olsza, brzoza, trawy, bylica, oliwka, ambrozja – każdy z paskiem poziomu.',
            chart: 'Wykres dolny: 24-godzinne maksimum dla wszystkich typów.',
            europe: 'Dane pochodzą z CAMS i są zazwyczaj dostępne tylko w Europie. Poza Europą możesz zobaczyć « Brak danych ».',
        },
        glance: {
            title: 'Widok Glance',
            what: 'Kompaktowy Glance na tarczy: bieżąca wartość AQI, kategoria i skala kolorów ze znacznikiem.',
            mono: 'Na monochromatycznych zegarkach Instinct używany jest pasek postępu zamiast skali kolorów.',
        },
    },
    controls: {
        nav: {
            title: 'Nawigacja między stronami',
            swipe: 'Ekran dotykowy: przesuń w lewo/prawo (lub góra/dół w zależności od zegarka), aby zmienić stronę.',
            buttons: 'Zegarki z przyciskami: Góra / Dół lub Następna / Poprzednia strona.',
            order: 'Pojawiają się tylko strony przypisane w ustawieniach (Strona 1–6). Puste sloty (« Brak ») są pomijane.',
        },
        refresh: {
            title: 'Ręczne odświeżenie',
            text: 'Naciśnij Select / Enter / Start, aby natychmiast pobrać nowe dane. Zegarek potrzebuje połączenia z telefonem (Bluetooth) do pobrania.',
            bg: 'W tle aplikacja również aktualizuje się automatycznie według interwału z ustawień (domyślnie 60 minut).',
        },
        menu: {
            title: 'Menu na zegarku (standard AQI)',
            text: 'Otwórz menu na zegarku, aby przełączać między europejskim AQI a US AQI. Zapisuje to samo ustawienie co Connect IQ Mobile – obowiązuje ostatnia zmiana.',
        },
        location: {
            title: 'Lokalizacja',
            text: 'Domyślnie aplikacja używa lokalizacji zegarka/telefonu. Możesz ją zastąpić alternatywnymi współrzędnymi w ustawieniach (przydatne dla jakości powietrza w innym miejscu).',
            gps: 'Jeśli brakuje lokalizacji lub jest nieaktualna: uzyskaj fix GPS (krótko uruchom aktywność lub użyj widgetu GPS), potem odśwież.',
        },
        connection: {
            title: 'Połączenie z telefonem',
            text: 'Żądania internetowe przechodzą przez sparowany telefon. Przy błędzie połączenia otwórz Garmin Connect, włącz Bluetooth i spróbuj ręcznego odświeżenia.',
        },
    },
    index: {
        intro: {
            title: 'Dwa standardy – jedna aplikacja',
            text: 'Atmos Air Quality może pokazywać europejski wskaźnik jakości powietrza (EAQI) lub US AQI (EPA). Liczba, skala kolorów i kategorie zmieniają się ze standardem – pomiary zanieczyszczeń pozostają te same.',
            switch: 'Przełącz w ustawieniach Connect IQ (« Standard AQI ») lub przez menu na zegarku.',
        },
        diff: {
            title: 'Główne różnice',
            scale: 'Skala: UE zazwyczaj ma niższe wartości bezwzględne (wskaźnik kończy się około 120). US AQI często sięga 300+ na wskaźniku.',
            thresholds: 'Progi kategorii różnią się – to samo powietrze może wyglądać na « umiarkowane » w jednym systemie i « niezdrowe dla wrażliwych grup » w drugim.',
            use: 'Używaj UE, jeśli znasz europejskie aplikacje/media; US, jeśli porównujesz z raportami EPA / AirNow.',
        },
        table: { range: 'Indeks', category: 'Kategoria' },
        eu: {
            title: 'Europejski AQI (EAQI)',
            text: 'Łączy kilka zanieczyszczeń w jeden indeks. Kategorie mniej więcej: Dobra → Zadowalająca → Umiarkowana → Zła → Bardzo zła → Ekstremalnie zła.',
            c0: 'Dobra',
            c1: 'Zadowalająca',
            c2: 'Umiarkowana',
            c3: 'Zła',
            c4: 'Bardzo zła',
            c5: 'Ekstremalnie zła',
        },
        us: {
            title: 'US AQI (EPA)',
            text: 'Powszechnie używany w USA. Kategorie: Good → Moderate → Unhealthy for Sensitive Groups → Unhealthy → Very Unhealthy → Hazardous.',
            c0: 'Good',
            c1: 'Moderate',
            c2: 'Unhealthy for Sensitive Groups',
            c3: 'Unhealthy',
            c4: 'Very Unhealthy',
            c5: 'Hazardous',
        },
        warning: 'Nie mieszaj dwóch liczb. « 80 » w skali UE to nie to samo co « 80 » w skali US.',
    },
    values: {
        intro: {
            title: 'Co oznaczają wartości?',
            text: 'AQI to indeks podsumowujący. Strony zanieczyszczeń i wykres cząstek pokazują, który składnik jest podwyższony. Jednostki cząstek i gazów to zazwyczaj µg/m³, chyba że podano inaczej.',
        },
        pm25: {
            title: 'PM2.5',
            text: 'Drobne cząstki mniejsze niż 2,5 µm. Wnikają głęboko w płuca. Często główny czynnik złego AQI w miastach oraz przy dymie z drewna / pożarach.',
        },
        pm10: {
            title: 'PM10',
            text: 'Grubsze cząstki do 10 µm (pył, fragmenty pyłków, ścieranie nawierzchni). Mniej głęboko niż PM2.5, ale istotne dla podrażnienia dróg oddechowych.',
        },
        o3: {
            title: 'Ozon (O₃)',
            text: 'Ozon przyziemny powstaje z gazów drogowych/przemysłowych w świetle słonecznym. Często wyższy w słoneczne popołudnia; może drażnić drogi oddechowe podczas sportu na świeżym powietrzu.',
        },
        no2: {
            title: 'Dwutlenek azotu (NO₂)',
            text: 'Typowo związany z ruchem i spalaniem. Podwyższony przy ruchliwych drogach; część europejskiego obliczenia indeksu.',
        },
        so2: {
            title: 'Dwutlenek siarki (SO₂)',
            text: 'Ze spalania paliw siarkowych i niektórych źródeł przemysłowych. Zazwyczaj niższy niż PM lub NO₂ w wielu miastach Europy Zachodniej.',
        },
        co: {
            title: 'Tlenek węgla (CO)',
            text: 'Niepełne spalanie (ruch, ogrzewanie). Niebezpieczny przy wysokich stężeniach w pomieszczeniach; na zewnątrz zazwyczaj znacznie niższy.',
        },
        dust: {
            title: 'Pył',
            text: 'Obciążenie pyłem mineralnym / saharyjskim. Skoki mogą podnosić wartości PM na dużych obszarach daleko od źródła.',
        },
        uv: {
            title: 'Indeks UV',
            text: 'Indeks ryzyka oparzeń słonecznych (nie zanieczyszczenie powietrza). Przydatny przy planowaniu aktywności na świeżym powietrzu w pogodne dni.',
        },
        aod: {
            title: 'AOD / widoczność',
            text: 'Aerosol Optical Depth opisuje, jak « mglista » jest atmosfera. Aplikacja przelicza to na przybliżoną widoczność w km (jasno → gęsta mgła).',
        },
        pollen: {
            title: 'Pyłki',
            text: 'Liczby pyłków istotne dla alergii (Europa). Poziomy według gatunku; wykres śledzi dzienny szczyt między typami.',
        },
        disclaimer: 'Ta strona ma charakter edukacyjny, nie medyczny. W decyzjach zdrowotnych kieruj się oficjalnymi lokalnymi zaleceniami dotyczącymi jakości powietrza.',
    },
    settings: {
        intro: {
            title: 'Przegląd ustawień',
            text: 'Otwórz ustawienia aplikacji w Garmin Connect IQ (telefon) lub Garmin Express. Standard AQI można też zmienić z menu na zegarku.',
        },
        aqStd: {
            title: 'Standard AQI',
            text: 'Europejski lub US. Wpływa na liczbę, kolory i kategorie wszędzie (widok główny, wykresy, Glance, komplikacje).',
        },
        pages: {
            title: 'Strony 1–6',
            text: 'Każdy slot może być: Aktualna, Prognoza 24 h, Prognoza 5 dni, Pył, Cząstki, Mgła/widoczność, Pyłki – lub Brak, aby ukryć slot.',
            tip: 'Wskazówka: ustaw Aktualna na pierwszej pozycji, potem najczęściej używane prognozy. Ukryj nieużywane strony, aby mniej przesuwać.',
        },
        series: {
            title: 'Seria cząstek A / B',
            text: 'Wybierz dwie metryki na wykres porównawczy (PM2.5, PM10, Pył, UV, O₃, NO₂, CO, SO₂, AOD). Ustaw serię na « Brak », aby ukryć; jedna seria używa pełnej szerokości.',
        },
        location: {
            title: 'Alternatywna lokalizacja',
            text: 'Opcjonalna szerokość / długość geograficzna. Zostaw 0 / 0 dla lokalizacji zegarka. Ustaw obie, aby przypiąć stałe miejsce (dom, praca, wakacje).',
        },
        interval: {
            title: 'Interwał aktualizacji',
            text: 'Jak często usługa w tle ma odświeżać (minuty). Minimum 10, maksimum 720. Domyślnie 60. Krótsze interwały zużywają więcej danych i baterii.',
        },
        comp: {
            title: 'Komplikacje',
            forDevs: 'Dla deweloperów:',
            intro: 'Ta aplikacja publikuje komplikacje do użycia na tarczach',
            aqi: '"AtmosAirAQI": Bieżący indeks jakości powietrza (indeks UE lub US, w zależności od wyboru użytkownika)',
            aqiRange: 'Zawiera „Range” wskazujący, gdzie mieści się ta wartość.',
            ts: '"AtmosAirTs": Znacznik czasu UTC bieżącej wartości',
            longLabel: 'Long Label: „AtmosAirAQI”',
        },
        limits: {
            title: 'Ograniczenia urządzenia (mała pamięć)',
            warning: 'Na niektórych starszych / małopamięciowych modelach (np. Instinct 2, Forerunner 55, Fenix 6 i podobne) dodatkowe strony Pył, Cząstki, Mgła i Pyłki są niedostępne. Aktualna + prognozy pozostają.',
            text: 'To zamierzone, aby widget pozostał stabilny w limitach pamięci Garmin na tych urządzeniach.',
        },
        supportBefore: 'Pytania lub problemy? Skorzystaj z',
        supportLink: 'formularza kontaktowego',
        supportAfter: '.',
    },
};
export const aqproCs = {
    views: {
        intro: 'Atmos Air Quality zobrazuje aktuální kvalitu ovzduší a předpovědi jako widget Garmin. Přejeďte prstem nebo použijte Nahoru/Dolů pro přepínání stránek povolených v nastavení.',
        current: {
            title: 'Aktuální kvalita ovzduší',
            gauge: 'Velký barevný ukazatel: aktuální AQI (EU nebo US podle nastavení). Značka ukazuje pozici na stupnici.',
            category: 'Štítek kategorie (např. Dobrá / Střední / Špatná) pod nebo vedle hodnoty.',
            pollutants: 'Hodnoty PM2.5, PM10 a O₃ jsou zobrazeny jako rychlý přehled.',
            location: 'Název místa a jak stará jsou data.',
        },
        hourly: {
            title: '24hodinová předpověď',
            bars: 'Sloupcový graf AQI na příštích 24 hodin od aktuální hodiny.',
            colors: 'Barvy sloupců odpovídají stejným kategoriím AQI jako hlavní ukazatel.',
            axis: 'Čas jde zleva → doprava (teď → později).',
        },
        daily: {
            title: '5denní předpověď',
            range: 'Každý den zobrazuje očekávané maximum a minimum AQI.',
            use: 'Užitečné pro plánování venkovních aktivit v nadcházejících dnech.',
        },
        dust: {
            title: 'Saharský prach / prach',
            current: 'Aktuální úroveň prachu plus 24hodinový trend.',
            daily: 'Vícedenní výhled prachu, pokud jsou data k dispozici.',
            note: 'Obzvláště relevantní během saharských prachových událostí zvyšujících hodnoty PM.',
        },
        particles: {
            title: 'Porovnání částic',
            dual: 'Dvojitý graf porovnávající dvě série z nastavení (např. PM2.5 vs PM10 nebo O₃ vs NO₂).',
            single: 'Pokud je vybrána jen jedna série, využije celou šířku.',
            series: 'Dostupné série: PM2.5, PM10, Prach, UV, O₃, NO₂, CO, SO₂, AOD.',
        },
        visibility: {
            title: 'Opar / viditelnost (AOD)',
            aod: 'Na základě Aerosol Optical Depth (AOD) – jak moc aerosoly zatemňují atmosféru.',
            km: 'Převedeno na přibližnou viditelnost v kilometrech.',
            chart: '24hodinový graf: vyšší sloupce ≈ lepší viditelnost; barva ukazuje hustotu oparu.',
        },
        pollen: {
            title: 'Pyly (Evropa)',
            species: 'Šest typů: olše, bříza, trávy, pelyněk, olivovník, ambrózie – každý s ukazatelem úrovně.',
            chart: 'Spodní graf: 24hodinové maximum napříč všemi typy.',
            europe: 'Data pocházejí z CAMS a jsou obvykle dostupná pouze v Evropě. Mimo Evropu můžete vidět « Žádná data ».',
        },
        glance: {
            title: 'Glance zobrazení',
            what: 'Kompaktní Glance na ciferníku: aktuální hodnota AQI, kategorie a barevná stupnice se značkou.',
            mono: 'Na monochromatických hodinkách Instinct se místo barevné stupnice používá ukazatel průběhu.',
        },
    },
    controls: {
        nav: {
            title: 'Navigace mezi stránkami',
            swipe: 'Dotyková obrazovka: přejeďte vlevo/vpravo (nebo nahoru/dolů podle hodinek) pro změnu stránky.',
            buttons: 'Hodinky s tlačítky: Nahoru / Dolů nebo Další / Předchozí stránka.',
            order: 'Zobrazí se pouze stránky přiřazené v nastavení (Stránka 1–6). Prázdné sloty (« Žádná ») se přeskočí.',
        },
        refresh: {
            title: 'Ruční obnovení',
            text: 'Stiskněte Select / Enter / Start pro okamžité stažení nových dat. Hodinky potřebují spojení s telefonem (Bluetooth) pro stažení.',
            bg: 'Na pozadí aplikace také automaticky aktualizuje podle intervalu z nastavení (výchozí 60 minut).',
        },
        menu: {
            title: 'Menu na hodinkách (standard AQI)',
            text: 'Otevřete menu na hodinkách pro přepínání mezi evropským AQI a US AQI. Zapisuje stejné nastavení jako Connect IQ Mobile – platí poslední změna.',
        },
        location: {
            title: 'Poloha',
            text: 'Ve výchozím nastavení aplikace používá polohu hodinek/telefonu. V nastavení můžete zadat alternativní souřadnice (užitečné pro kvalitu ovzduší jinde).',
            gps: 'Chybí-li poloha nebo je zastaralá: získejte GPS fix (krátce spusťte aktivitu nebo použijte GPS widget), pak obnovte.',
        },
        connection: {
            title: 'Spojení s telefonem',
            text: 'Internetové požadavky jdou přes spárovaný telefon. Při chybě spojení otevřete Garmin Connect, nechte Bluetooth zapnutý a zkuste ruční obnovení.',
        },
    },
    index: {
        intro: {
            title: 'Dva standardy – jedna aplikace',
            text: 'Atmos Air Quality může zobrazovat evropský index kvality ovzduší (EAQI) nebo US AQI (EPA). Číslo, barevná stupnice a kategorie se mění se standardem – podkladová měření znečišťujících látek zůstávají stejná.',
            switch: 'Přepněte v nastavení Connect IQ (« Standard AQI ») nebo přes menu na hodinkách.',
        },
        diff: {
            title: 'Hlavní rozdíly',
            scale: 'Stupnice: EU obvykle pracuje s nižšími absolutními hodnotami (ukazatel končí kolem 120). US AQI na ukazateli často sahá k 300+.',
            thresholds: 'Prahy kategorií se liší – stejné ovzduší může v jednom systému vypadat « středně » a v druhém « nezdravé pro citlivé skupiny ».',
            use: 'Použijte EU, pokud znáte evropské aplikace/média; US, pokud porovnáváte s reporty EPA / AirNow.',
        },
        table: { range: 'Index', category: 'Kategorie' },
        eu: {
            title: 'Evropský AQI (EAQI)',
            text: 'Kombinuje několik znečišťujících látek do jednoho indexu. Kategorie zhruba: Dobrá → Uspokojivá → Střední → Špatná → Velmi špatná → Extrémně špatná.',
            c0: 'Dobrá',
            c1: 'Uspokojivá',
            c2: 'Střední',
            c3: 'Špatná',
            c4: 'Velmi špatná',
            c5: 'Extrémně špatná',
        },
        us: {
            title: 'US AQI (EPA)',
            text: 'Široce používaný v USA. Kategorie: Good → Moderate → Unhealthy for Sensitive Groups → Unhealthy → Very Unhealthy → Hazardous.',
            c0: 'Good',
            c1: 'Moderate',
            c2: 'Unhealthy for Sensitive Groups',
            c3: 'Unhealthy',
            c4: 'Very Unhealthy',
            c5: 'Hazardous',
        },
        warning: 'Nemíchejte dvě čísla. « 80 » na EU stupnici není totéž co « 80 » na US stupnici.',
    },
    values: {
        intro: {
            title: 'Co znamenají hodnoty?',
            text: 'AQI je souhrnný index. Stránky znečišťujících látek a graf částic ukazují, která složka je zvýšená. Jednotky částic a plynů jsou obvykle µg/m³, pokud není uvedeno jinak.',
        },
        pm25: {
            title: 'PM2.5',
            text: 'Jemné částice menší než 2,5 µm. Vnikají hluboko do plic. Často hlavní faktor špatného AQI ve městech a při dýmu z dřeva / požárech.',
        },
        pm10: {
            title: 'PM10',
            text: 'Hrubší částice do 10 µm (prach, fragmenty pylu, opotřebení vozovky). Méně hluboko než PM2.5, ale relevantní pro podráždění dýchacích cest.',
        },
        o3: {
            title: 'Ozon (O₃)',
            text: 'Přízemní ozon vzniká z dopravních/průmyslových plynů na slunci. Často vyšší za slunečných odpolední; může dráždit dýchací cesty při venkovním sportu.',
        },
        no2: {
            title: 'Oxid dusičitý (NO₂)',
            text: 'Typicky spojen s dopravou a spalováním. Zvýšený u rušných silnic; součást evropského výpočtu indexu.',
        },
        so2: {
            title: 'Oxid siřičitý (SO₂)',
            text: 'Ze spalování síra obsahujících paliv a některých průmyslových zdrojů. Ve většině západoevropských měst obvykle nižší než PM nebo NO₂.',
        },
        co: {
            title: 'Oxid uhelnatý (CO)',
            text: 'Neúplné spalování (doprava, vytápění). Nebezpečný při vysokých vnitřních koncentracích; venku obvykle mnohem nižší.',
        },
        dust: {
            title: 'Prach',
            text: 'Zatížení minerálním / saharským prachem. Špičky mohou zvyšovat hodnoty PM na velkých oblastech daleko od zdroje.',
        },
        uv: {
            title: 'UV index',
            text: 'Index rizika spálení (není znečišťující látkou). Užitečný vedle plánování kvality ovzduší venku za jasného počasí.',
        },
        aod: {
            title: 'AOD / viditelnost',
            text: 'Aerosol Optical Depth popisuje, jak « mlhavá » je atmosféra. Aplikace to převádí na přibližnou viditelnost v km (jasno → hustý opar).',
        },
        pollen: {
            title: 'Pyly',
            text: 'Počty pylů relevantní pro alergie (Evropa). Úrovně podle druhu; graf sleduje denní maximum napříč typy.',
        },
        disclaimer: 'Tato stránka je informativní, ne lékařská rada. Pro zdravotní rozhodnutí se řiďte oficiálními místními doporučeními kvality ovzduší.',
    },
    settings: {
        intro: {
            title: 'Přehled nastavení',
            text: 'Otevřete nastavení aplikace v Garmin Connect IQ (telefon) nebo Garmin Express. Standard AQI lze změnit také z menu na hodinkách.',
        },
        aqStd: {
            title: 'Standard AQI',
            text: 'Evropský nebo US. Ovlivňuje číslo, barvy a kategorie všude (hlavní zobrazení, grafy, Glance, komplikace).',
        },
        pages: {
            title: 'Stránky 1–6',
            text: 'Každý slot může být: Aktuální, 24h předpověď, 5denní předpověď, Prach, Částice, Opar/viditelnost, Pyly – nebo Žádná pro skrytí slotu.',
            tip: 'Tip: dejte Aktuální jako první, pak nejpoužívanější předpovědi. Skryjte nepoužívané stránky pro méně přejíždění.',
        },
        series: {
            title: 'Série částic A / B',
            text: 'Vyberte dvě metriky pro porovnávací graf (PM2.5, PM10, Prach, UV, O₃, NO₂, CO, SO₂, AOD). Nastavte sérii na « Žádná » pro skrytí; jedna série využije celou šířku.',
        },
        location: {
            title: 'Alternativní poloha',
            text: 'Volitelná zeměpisná šířka / délka. Nechte 0 / 0 pro polohu hodinek. Nastavte obě pro pevné místo (domov, práce, dovolená).',
        },
        interval: {
            title: 'Interval aktualizace',
            text: 'Jak často má služba na pozadí obnovovat (minuty). Minimum 10, maximum 720. Výchozí 60. Kratší intervaly spotřebují více dat a baterie.',
        },
        comp: {
            title: 'Komplikace',
            forDevs: 'Pro vývojáře:',
            intro: 'Tato aplikace publikuje komplikace pro použití na cifernících',
            aqi: '"AtmosAirAQI": Aktuální index kvality ovzduší (index EU nebo US podle volby uživatele)',
            aqiRange: 'Obsahuje „Range“ ukazující, kam tato hodnota spadá.',
            ts: '"AtmosAirTs": UTC časové razítko aktuální hodnoty',
            longLabel: 'Long Label: „AtmosAirAQI“',
        },
        limits: {
            title: 'Limity zařízení (málo paměti)',
            warning: 'Na některých starších / málo paměťových modelech (např. Instinct 2, Forerunner 55, Fenix 6 a podobné) nejsou k dispozici extra stránky Prach, Částice, Opar a Pyly. Aktuální + předpovědi zůstávají.',
            text: 'Je to záměrné, aby widget zůstal stabilní v rámci paměťových limitů Garmin na těchto zařízeních.',
        },
        supportBefore: 'Dotazy nebo problémy? Použijte',
        supportLink: 'kontaktní formulář',
        supportAfter: '.',
    },
};
export const aqproRu = {
    views: {
        intro: 'Atmos Air Quality показывает текущее качество воздуха и прогнозы в виде виджета Garmin. Проводите пальцем или используйте Вверх/Вниз для переключения между страницами, включёнными в настройках.',
        current: {
            title: 'Текущее качество воздуха',
            gauge: 'Большой цветной индикатор: текущий AQI (EU или US в зависимости от настройки). Маркер показывает положение на шкале.',
            category: 'Метка категории (напр. Хорошо / Умеренно / Плохо) под или рядом со значением.',
            pollutants: 'Значения PM2.5, PM10 и O₃ показаны для быстрой справки.',
            location: 'Название места и давность обновления данных.',
        },
        hourly: {
            title: 'Прогноз на 24 часа',
            bars: 'Столбчатая диаграмма AQI на следующие 24 часа, начиная с текущего часа.',
            colors: 'Цвета столбцов соответствуют тем же категориям AQI, что и основной индикатор.',
            axis: 'Время идёт слева → направо (сейчас → позже).',
        },
        daily: {
            title: 'Прогноз на 5 дней',
            range: 'Каждый день показывает ожидаемый максимум и минимум AQI.',
            use: 'Полезно для планирования активности на свежем воздухе в ближайшие дни.',
        },
        dust: {
            title: 'Сахарская пыль / пыль',
            current: 'Текущий уровень пыли плюс 24-часовой тренд.',
            daily: 'Многодневный прогноз пыли при наличии данных.',
            note: 'Особенно актуально во время сахарских пылевых событий, повышающих уровни PM.',
        },
        particles: {
            title: 'Сравнение частиц',
            dual: 'Двойной график сравнивает две серии из настроек (напр. PM2.5 vs PM10 или O₃ vs NO₂).',
            single: 'Если выбрана только одна серия, используется вся ширина.',
            series: 'Доступные серии: PM2.5, PM10, Пыль, UV, O₃, NO₂, CO, SO₂, AOD.',
        },
        visibility: {
            title: 'Дымка / видимость (AOD)',
            aod: 'На основе Aerosol Optical Depth (AOD) – насколько аэрозоли затемняют атмосферу.',
            km: 'Преобразовано в приблизительную видимость в километрах.',
            chart: '24-часовой график: более высокие столбцы ≈ лучшая видимость; цвет указывает плотность дымки.',
        },
        pollen: {
            title: 'Пыльца (Европа)',
            species: 'Шесть типов: ольха, берёза, злаки, полынь, олива, амброзия – каждый с полосой уровня.',
            chart: 'Нижний график: 24-часовой максимум по всем типам.',
            europe: 'Данные от CAMS обычно доступны только в Европе. Вне Европы может отображаться « Нет данных ».',
        },
        glance: {
            title: 'Glance-вид',
            what: 'Компактный Glance на циферблате: текущее значение AQI, категория и цветовая шкала с маркером.',
            mono: 'На монохромных часах Instinct вместо цветовой шкалы используется индикатор прогресса.',
        },
    },
    controls: {
        nav: {
            title: 'Навигация между страницами',
            swipe: 'Сенсорный экран: проведите влево/вправо (или вверх/вниз в зависимости от часов) для смены страницы.',
            buttons: 'Часы с кнопками: Вверх / Вниз или Следующая / Предыдущая страница.',
            order: 'Отображаются только страницы, назначенные в настройках (Страница 1–6). Пустые слоты (« Нет ») пропускаются.',
        },
        refresh: {
            title: 'Ручное обновление',
            text: 'Нажмите Select / Enter / Start для немедленного запроса новых данных. Часам нужно соединение с телефоном (Bluetooth) для загрузки.',
            bg: 'В фоне приложение также автоматически обновляется по интервалу из настроек (по умолчанию 60 минут).',
        },
        menu: {
            title: 'Меню на часах (стандарт AQI)',
            text: 'Откройте меню на часах для переключения между европейским AQI и US AQI. Записывает ту же настройку, что и Connect IQ Mobile – действует последнее изменение.',
        },
        location: {
            title: 'Местоположение',
            text: 'По умолчанию приложение использует местоположение часов/телефона. Можно задать альтернативные координаты в настройках (полезно для качества воздуха в другом месте).',
            gps: 'Если местоположение отсутствует или устарело: получите GPS-фикс (кратко запустите активность или используйте GPS-виджет), затем обновите.',
        },
        connection: {
            title: 'Соединение с телефоном',
            text: 'Интернет-запросы идут через сопряжённый телефон. При ошибке соединения откройте Garmin Connect, включите Bluetooth и попробуйте ручное обновление.',
        },
    },
    index: {
        intro: {
            title: 'Два стандарта – одно приложение',
            text: 'Atmos Air Quality может показывать европейский индекс качества воздуха (EAQI) или US AQI (EPA). Число, цветовая шкала и категории меняются со стандартом – базовые измерения загрязнителей остаются теми же.',
            switch: 'Переключайте в настройках Connect IQ (« Стандарт AQI ») или через меню на часах.',
        },
        diff: {
            title: 'Основные различия',
            scale: 'Шкала: EU обычно использует более низкие абсолютные значения (индикатор заканчивается около 120). US AQI часто доходит до 300+ на индикаторе.',
            thresholds: 'Пороги категорий различаются – один и тот же воздух может выглядеть « умеренным » в одной системе и « нездоровым для чувствительных групп » в другой.',
            use: 'Используйте EU, если привыкли к европейским приложениям/СМИ; US – если сравниваете с отчётами EPA / AirNow.',
        },
        table: { range: 'Индекс', category: 'Категория' },
        eu: {
            title: 'Европейский AQI (EAQI)',
            text: 'Объединяет несколько загрязнителей в один индекс. Категории примерно: Хорошо → Удовлетворительно → Умеренно → Плохо → Очень плохо → Крайне плохо.',
            c0: 'Хорошо',
            c1: 'Удовлетворительно',
            c2: 'Умеренно',
            c3: 'Плохо',
            c4: 'Очень плохо',
            c5: 'Крайне плохо',
        },
        us: {
            title: 'US AQI (EPA)',
            text: 'Широко используется в США. Категории: Good → Moderate → Unhealthy for Sensitive Groups → Unhealthy → Very Unhealthy → Hazardous.',
            c0: 'Good',
            c1: 'Moderate',
            c2: 'Unhealthy for Sensitive Groups',
            c3: 'Unhealthy',
            c4: 'Very Unhealthy',
            c5: 'Hazardous',
        },
        warning: 'Не смешивайте два числа. « 80 » по шкале EU – это не то же самое, что « 80 » по шкале US.',
    },
    values: {
        intro: {
            title: 'Что означают значения?',
            text: 'AQI – сводный индекс. Страницы загрязнителей и график частиц показывают, какой компонент повышен. Единицы для частиц и газов обычно µg/m³, если не указано иное.',
        },
        pm25: {
            title: 'PM2.5',
            text: 'Мелкие частицы менее 2,5 µm. Проникают глубоко в лёгкие. Часто главный фактор плохого AQI в городах и при дыме от дров / лесных пожарах.',
        },
        pm10: {
            title: 'PM10',
            text: 'Более крупные частицы до 10 µm (пыль, фрагменты пыльцы, износ дорог). Менее глубоко, чем PM2.5, но важны для раздражения дыхательных путей.',
        },
        o3: {
            title: 'Озон (O₃)',
            text: 'Приземный озон образуется из выхлопных/промышленных газов на солнце. Часто выше в солнечные послеобеденные часы; может раздражать дыхательные пути при занятиях на свежем воздухе.',
        },
        no2: {
            title: 'Диоксид азота (NO₂)',
            text: 'Обычно связан с транспортом и сгоранием. Повышен у оживлённых дорог; часть европейского расчёта индекса.',
        },
        so2: {
            title: 'Диоксид серы (SO₂)',
            text: 'От сжигания серосодержащих топлив и некоторых промышленных источников. Обычно ниже PM или NO₂ во многих городах Западной Европы.',
        },
        co: {
            title: 'Монооксид углерода (CO)',
            text: 'Неполное сгорание (транспорт, отопление). Опасен при высоких концентрациях в помещении; на улице обычно значительно ниже.',
        },
        dust: {
            title: 'Пыль',
            text: 'Минеральная / сахарская пылевая нагрузка. Пики могут повышать PM на больших территориях далеко от источника.',
        },
        uv: {
            title: 'UV-индекс',
            text: 'Индекс риска солнечных ожогов (не загрязнитель воздуха). Удобен при планировании активности на свежем воздухе в ясные дни.',
        },
        aod: {
            title: 'AOD / видимость',
            text: 'Aerosol Optical Depth описывает, насколько « дымчатая » атмосфера. Приложение переводит это в приблизительную видимость в км (ясно → плотная дымка).',
        },
        pollen: {
            title: 'Пыльца',
            text: 'Количество пыльцы, важное для аллергии (Европа). Уровни по видам; график отслеживает суточный пик по всем типам.',
        },
        disclaimer: 'Эта страница носит информационный характер, а не медицинский совет. Для решений о здоровье следуйте официальным местным рекомендациям по качеству воздуха.',
    },
    settings: {
        intro: {
            title: 'Обзор настроек',
            text: 'Откройте настройки приложения в Garmin Connect IQ (телефон) или Garmin Express. Стандарт AQI также можно изменить через меню на часах.',
        },
        aqStd: {
            title: 'Стандарт AQI',
            text: 'Европейский или US. Влияет на число, цвета и категории везде (основной вид, графики, Glance, осложнения).',
        },
        pages: {
            title: 'Страницы 1–6',
            text: 'Каждый слот может быть: Текущая, Прогноз 24 ч, Прогноз 5 дней, Пыль, Частицы, Дымка/видимость, Пыльца – или Нет для скрытия слота.',
            tip: 'Совет: поставьте Текущая первой, затем наиболее используемые прогнозы. Скрывайте неиспользуемые страницы, чтобы меньше листать.',
        },
        series: {
            title: 'Серия частиц A / B',
            text: 'Выберите две метрики для сравнительного графика (PM2.5, PM10, Пыль, UV, O₃, NO₂, CO, SO₂, AOD). Установите « Нет », чтобы скрыть серию; одна серия использует всю ширину.',
        },
        location: {
            title: 'Альтернативное местоположение',
            text: 'Необязательные широта / долгота. Оставьте 0 / 0 для местоположения часов. Установите обе для фиксированного места (дом, работа, отпуск).',
        },
        interval: {
            title: 'Интервал обновления',
            text: 'Как часто фоновая служба должна обновлять (минуты). Минимум 10, максимум 720. По умолчанию 60. Более короткие интервалы расходуют больше данных и батареи.',
        },
        comp: {
            title: 'Complication',
            forDevs: 'Для разработчиков:',
            intro: 'Это приложение публикует complication для использования на циферблатах',
            aqi: '"AtmosAirAQI": Текущий индекс качества воздуха (индекс ЕС или США в зависимости от выбора пользователя)',
            aqiRange: 'Включает «Range», указывающий, куда попадает это значение.',
            ts: '"AtmosAirTs": UTC-метка времени текущего значения',
            longLabel: 'Long Label: «AtmosAirAQI»',
        },
        limits: {
            title: 'Ограничения устройства (мало памяти)',
            warning: 'На некоторых старых / малопамятных моделях (напр. Instinct 2, Forerunner 55, Fenix 6 и подобные) дополнительные страницы Пыль, Частицы, Дымка и Пыльца недоступны. Текущая + прогнозы остаются.',
            text: 'Это сделано намеренно, чтобы виджет оставался стабильным в пределах памяти Garmin на этих устройствах.',
        },
        supportBefore: 'Вопросы или проблемы? Используйте',
        supportLink: 'форму обратной связи',
        supportAfter: '.',
    },
};
export const aqproUk = {
    views: {
        intro: 'Atmos Air Quality показує поточну якість повітря та прогнози у віджеті Garmin. Проведіть або використовуйте Вгору/Вниз, щоб перемикатися між сторінками, увімкненими в налаштуваннях.',
        current: {
            title: 'Поточна якість повітря',
            gauge: 'Великий кольоровий індикатор: поточний AQI (EU або US залежно від налаштування). Маркер показує ваше положення на шкалі.',
            category: 'Мітка категорії (напр. Добре / Помірно / Погано) під або біля значення.',
            pollutants: 'PM2.5, PM10 та O₃ показуються як швидкий довідник.',
            location: 'Назва місця та час останнього оновлення даних.',
        },
        hourly: {
            title: '24-годинний прогноз',
            bars: 'Стовпчикова діаграма AQI на наступні 24 години, починаючи з поточної години.',
            colors: 'Кольори стовпців відповідають тим самим категоріям AQI, що й основний індикатор.',
            axis: 'Час іде зліва → направо (зараз → пізніше).',
        },
        daily: {
            title: '5-денний прогноз',
            range: 'Кожен день показує очікуваний максимум і мінімум AQI.',
            use: 'Корисно для планування активностей на свіжому повітрі у найближчі дні.',
        },
        dust: {
            title: 'Сахарський пил / пил',
            current: 'Поточний рівень пилу плюс 24-годинна тенденція.',
            daily: 'Багатоденний прогноз пилу, коли дані доступні.',
            note: 'Особливо актуально під час подій сахарського пилу, що підвищують рівні PM.',
        },
        particles: {
            title: 'Порівняння частинок',
            dual: 'Подвійний графік порівнює дві серії з налаштувань (напр. PM2.5 vs PM10 або O₃ vs NO₂).',
            single: 'Якщо обрано лише одну серію, використовується вся ширина.',
            series: 'Доступні серії: PM2.5, PM10, Пил, UV, O₃, NO₂, CO, SO₂, AOD.',
        },
        visibility: {
            title: 'Серпанок / видимість (AOD)',
            aod: 'На основі Aerosol Optical Depth (AOD) – наскільки аерозолі затемнюють атмосферу.',
            km: 'Перетворено на приблизну видимість у кілометрах.',
            chart: '24-годинний графік: вищі стовпці ≈ краща видимість; колір показує щільність серпанку.',
        },
        pollen: {
            title: 'Пилок (Європа)',
            species: 'Шість типів: вільха, береза, трава, полин, олива, амброзія – кожен зі смугою рівня.',
            chart: 'Нижній графік: 24-годинний максимум по всіх типах.',
            europe: 'Дані від CAMS зазвичай доступні лише в Європі. Поза Європою може з\'явитися «Немає даних».',
        },
        glance: {
            title: 'Glance-вид',
            what: 'Компактний Glance на циферблаті: поточне значення AQI, категорія та кольорова шкала з маркером.',
            mono: 'На монохромних годинниках Instinct замість кольорової шкали використовується індикатор прогресу.',
        },
    },
    controls: {
        nav: {
            title: 'Навігація між сторінками',
            swipe: 'Сенсорний екран: проведіть вліво/вправо (або вгору/вниз залежно від годинника) для зміни сторінки.',
            buttons: 'Годинники з кнопками: Вгору / Вниз або Наступна / Попередня сторінка.',
            order: 'З\'являються лише сторінки, призначені в налаштуваннях (Сторінка 1–6). Порожні слоти («Немає») пропускаються.',
        },
        refresh: {
            title: 'Ручне оновлення',
            text: 'Натисніть Select / Enter / Start для негайного запиту нових даних. Годиннику потрібне з\'єднання з телефоном (Bluetooth) для завантаження.',
            bg: 'У фоні додаток також автоматично оновлюється за інтервалом з налаштувань (за замовчуванням 60 хвилин).',
        },
        menu: {
            title: 'Меню на годиннику (стандарт AQI)',
            text: 'Відкрийте меню на годиннику для перемикання між європейським AQI та US AQI. Записує ту саму настройку, що й Connect IQ Mobile – діє остання зміна.',
        },
        location: {
            title: 'Місцезнаходження',
            text: 'За замовчуванням додаток використовує місцезнаходження годинника/телефона. Можна задати альтернативні координати в налаштуваннях (корисно для якості повітря в іншому місці).',
            gps: 'Якщо місцезнаходження відсутнє або застаріле: отримайте GPS-фікс (коротко запустіть активність або використайте GPS-віджет), потім оновіть.',
        },
        connection: {
            title: 'З\'єднання з телефоном',
            text: 'Інтернет-запити йдуть через спарений телефон. При помилці з\'єднання відкрийте Garmin Connect, увімкніть Bluetooth і спробуйте ручне оновлення.',
        },
    },
    index: {
        intro: {
            title: 'Два стандарти – один додаток',
            text: 'Atmos Air Quality може показувати європейський індекс якості повітря (EAQI) або US AQI (EPA). Число, кольорова шкала та категорії змінюються зі стандартом – базові вимірювання забруднювачів залишаються тими самими.',
            switch: 'Перемикайте в налаштуваннях Connect IQ («Стандарт AQI») або через меню на годиннику.',
        },
        diff: {
            title: 'Основні відмінності',
            scale: 'Шкала: EU зазвичай використовує нижчі абсолютні значення (індикатор закінчується близько 120). US AQI часто доходить до 300+ на індикаторі.',
            thresholds: 'Пороги категорій різняться – те саме повітря може виглядати «помірним» в одній системі та «нездоровим для чутливих груп» в іншій.',
            use: 'Використовуйте EU, якщо звикли до європейських додатків/ЗМІ; US – якщо порівнюєте зі звітами EPA / AirNow.',
        },
        table: { range: 'Індекс', category: 'Категорія' },
        eu: {
            title: 'Європейський AQI (EAQI)',
            text: 'Об\'єднує кілька забруднювачів в один індекс. Категорії приблизно: Добре → Задовільно → Помірно → Погано → Дуже погано → Надзвичайно погано.',
            c0: 'Добре',
            c1: 'Задовільно',
            c2: 'Помірно',
            c3: 'Погано',
            c4: 'Дуже погано',
            c5: 'Надзвичайно погано',
        },
        us: {
            title: 'US AQI (EPA)',
            text: 'Широко використовується в США. Категорії: Good → Moderate → Unhealthy for Sensitive Groups → Unhealthy → Very Unhealthy → Hazardous.',
            c0: 'Good',
            c1: 'Moderate',
            c2: 'Unhealthy for Sensitive Groups',
            c3: 'Unhealthy',
            c4: 'Very Unhealthy',
            c5: 'Hazardous',
        },
        warning: 'Не змішуйте два числа. «80» за шкалою EU – це не те саме, що «80» за шкалою US.',
    },
    values: {
        intro: {
            title: 'Що означають значення?',
            text: 'AQI – зведений індекс. Сторінки забруднювачів і графік частинок показують, який компонент підвищений. Одиниці для частинок і газів зазвичай µg/m³, якщо не вказано інше.',
        },
        pm25: {
            title: 'PM2.5',
            text: 'Дрібні частинки менше 2,5 µm. Проникають глибоко в легені. Часто головний фактор поганого AQI в містах та під час диму від дров / лісових пожеж.',
        },
        pm10: {
            title: 'PM10',
            text: 'Більші частинки до 10 µm (пил, фрагменти пилку, знос доріг). Менш глибоко, ніж PM2.5, але важливі для подразнення дихальних шляхів.',
        },
        o3: {
            title: 'Озон (O₃)',
            text: 'Приземний озон утворюється з вихлопних/промислових газів на сонці. Часто вищий у сонячні післяобідні години; може подразнювати дихальні шляхи під час занять на свіжому повітрі.',
        },
        no2: {
            title: 'Діоксид азоту (NO₂)',
            text: 'Зазвичай пов\'язаний з транспортом і згорянням. Підвищений біля оживлених доріг; частина європейського розрахунку індексу.',
        },
        so2: {
            title: 'Діоксид сірки (SO₂)',
            text: 'Від згоряння сірковмісного палива та деяких промислових джерел. Зазвичай нижчий за PM або NO₂ у багатьох містах Західної Європи.',
        },
        co: {
            title: 'Монооксид вуглецю (CO)',
            text: 'Неповне згоряння (транспорт, опалення). Небезпечний при високих концентраціях у приміщенні; на вулиці зазвичай значно нижчий.',
        },
        dust: {
            title: 'Пил',
            text: 'Мінеральне / сахарське пилове навантаження. Сплески можуть підвищувати PM на великих територіях далеко від джерела.',
        },
        uv: {
            title: 'UV-індекс',
            text: 'Індекс ризику сонячних опіків (не забруднювач повітря). Зручний при плануванні активності на свіжому повітрі в ясні дні.',
        },
        aod: {
            title: 'AOD / видимість',
            text: 'Aerosol Optical Depth описує, наскільки «димчаста» атмосфера. Додаток переводить це в приблизну видимість у км (ясно → густий серпанок).',
        },
        pollen: {
            title: 'Пилок',
            text: 'Кількість пилку, важлива для алергії (Європа). Рівні за видами; графік відстежує добовий пік по всіх типах.',
        },
        disclaimer: 'Ця сторінка носить інформаційний характер, а не медичну пораду. Для рішень щодо здоров\'я дотримуйтесь офіційних місцевих рекомендацій з якості повітря.',
    },
    settings: {
        intro: {
            title: 'Огляд налаштувань',
            text: 'Відкрийте налаштування додатка в Garmin Connect IQ (телефон) або Garmin Express. Стандарт AQI також можна змінити через меню на годиннику.',
        },
        aqStd: {
            title: 'Стандарт AQI',
            text: 'Європейський або US. Впливає на число, кольори та категорії всюди (основний вид, графіки, Glance, ускладнення).',
        },
        pages: {
            title: 'Сторінки 1–6',
            text: 'Кожен слот може бути: Поточна, Прогноз 24 год, Прогноз 5 днів, Пил, Частинки, Серпанок/видимість, Пилок – або Немає для приховування слота.',
            tip: 'Порада: поставте Поточна першою, потім найбільш використовувані прогнози. Приховуйте невикористовувані сторінки, щоб менше гортати.',
        },
        series: {
            title: 'Серія частинок A / B',
            text: 'Оберіть дві метрики для порівняльного графіка (PM2.5, PM10, Пил, UV, O₃, NO₂, CO, SO₂, AOD). Встановіть «Немає», щоб приховати серію; одна серія використовує всю ширину.',
        },
        location: {
            title: 'Альтернативне місцезнаходження',
            text: 'Необов\'язкові широта / довгота. Залиште 0 / 0 для місцезнаходження годинника. Встановіть обидва для фіксованого місця (дім, робота, відпустка).',
        },
        interval: {
            title: 'Інтервал оновлення',
            text: 'Як часто фонова служба має оновлювати (хвилини). Мінімум 10, максимум 720. За замовчуванням 60. Коротші інтервали споживають більше даних і батареї.',
        },
        comp: {
            title: 'Complication',
            forDevs: 'Для розробників:',
            intro: 'Цей додаток публікує complication для використання на циферблатах',
            aqi: '"AtmosAirAQI": Поточний індекс якості повітря (індекс ЄС або США залежно від вибору користувача)',
            aqiRange: 'Містить «Range», що показує, куди потрапляє це значення.',
            ts: '"AtmosAirTs": UTC-мітка часу поточного значення',
            longLabel: 'Long Label: «AtmosAirAQI»',
        },
        limits: {
            title: 'Обмеження пристрою (мало пам\'яті)',
            warning: 'На деяких старих / малопам\'ятних моделях (напр. Instinct 2, Forerunner 55, Fenix 6 та подібні) додаткові сторінки Пил, Частинки, Серпанок і Пилок недоступні. Поточна + прогнози залишаються.',
            text: 'Це зроблено навмисно, щоб віджет залишався стабільним у межах пам\'яті Garmin на цих пристроях.',
        },
        supportBefore: 'Питання або проблеми? Скористайтеся',
        supportLink: 'формою зворотного зв\'язку',
        supportAfter: '.',
    },
};
export const aqproNo = {
    views: {
        intro: 'Atmos Air Quality viser gjeldende luftkvalitet og prognoser som et Garmin-widget. Sveip eller bruk Opp/Ned for å bytte mellom sidene du har aktivert i innstillingene.',
        current: {
            title: 'Gjeldende luftkvalitet',
            gauge: 'Stor farget måler: gjeldende AQI (EU eller US avhengig av innstilling). Markøren viser hvor du er på skalaen.',
            category: 'Kategorietikett (f.eks. God / Moderat / Dårlig) under eller ved siden av verdien.',
            pollutants: 'PM2.5, PM10 og O₃ vises som rask referanse.',
            location: 'Stedsnavn og hvor lenge siden dataene ble oppdatert.',
        },
        hourly: {
            title: '24-timers prognose',
            bars: 'Stolpediagram over AQI for de neste 24 timene, fra gjeldende time.',
            colors: 'Stolpefargene følger de samme AQI-kategoriene som hovedmåleren.',
            axis: 'Tiden går venstre → høyre (nå → senere).',
        },
        daily: {
            title: '5-dagers prognose',
            range: 'Hver dag viser forventet AQI-maksimum og -minimum.',
            use: 'Nyttig for planlegging av utendørsaktiviteter de kommende dagene.',
        },
        dust: {
            title: 'Sahara-støv / støv',
            current: 'Gjeldende støvnivå pluss 24-timers trend.',
            daily: 'Flerdagers støvutsikt når data er tilgjengelige.',
            note: 'Spesielt relevant under Sahara-støvhendelser som hever PM-nivåer.',
        },
        particles: {
            title: 'Partikkel-sammenligning',
            dual: 'Dobbelt diagram som sammenligner to serier du velger i innstillingene (f.eks. PM2.5 vs PM10 eller O₃ vs NO₂).',
            single: 'Hvis bare én serie er valgt, brukes hele bredden.',
            series: 'Tilgjengelige serier: PM2.5, PM10, Støv, UV, O₃, NO₂, CO, SO₂, AOD.',
        },
        visibility: {
            title: 'Dis / sikt (AOD)',
            aod: 'Basert på Aerosol Optical Depth (AOD) – hvor mye aerosoler demper atmosfæren.',
            km: 'Omregnet til omtrentlig sikt i kilometer.',
            chart: '24-timers diagram: høyere stolper ≈ bedre sikt; farge viser disstyrke.',
        },
        pollen: {
            title: 'Pollen (Europa)',
            species: 'Seks typer: or, bjørk, gress, malurt, oliven, ambrosia – hver med nivåstripe.',
            chart: 'Nederste diagram: 24-timers maksimum på tvers av alle typer.',
            europe: 'Data kommer fra CAMS og er vanligvis bare tilgjengelig i Europa. Utenfor Europa kan du se «Ingen data».',
        },
        glance: {
            title: 'Glance-visning',
            what: 'Kompakt urskive-glance: gjeldende AQI-verdi, kategori og fargeskala med markør.',
            mono: 'På monokrome Instinct-klokker brukes en fremdriftslinje i stedet for fargeskalaen.',
        },
    },
    controls: {
        nav: {
            title: 'Navigere mellom sider',
            swipe: 'Berøringsskjerm: sveip venstre/høyre (eller opp/ned avhengig av klokken) for å bytte side.',
            buttons: 'Knappeklokker: Opp / Ned eller Neste / Forrige side.',
            order: 'Bare sider du har tildelt i innstillingene (Side 1–6) vises. Tomme plasser («Ingen») hoppes over.',
        },
        refresh: {
            title: 'Manuell oppdatering',
            text: 'Trykk Select / Enter / Start for å be om nye data med en gang. Klokken trenger telefonforbindelse (Bluetooth) for nedlastingen.',
            bg: 'I bakgrunnen oppdaterer appen også automatisk med intervallet fra innstillingene (standard 60 minutter).',
        },
        menu: {
            title: 'Meny på klokken (AQI-standard)',
            text: 'Åpne menyen på klokken for å bytte mellom europeisk AQI og US AQI. Skriver samme innstilling som i Connect IQ Mobile – den siste endringen gjelder.',
        },
        location: {
            title: 'Posisjon',
            text: 'Som standard bruker appen klokkens/telefonens posisjon. Du kan overstyre med alternative koordinater i innstillingene (nyttig hvis du vil ha luftkvalitet for et annet sted).',
            gps: 'Hvis posisjonen mangler eller er utdatert: få GPS-fix (start en aktivitet kort eller bruk GPS-widget), deretter oppdater.',
        },
        connection: {
            title: 'Telefonforbindelse',
            text: 'Internettforespørsler går via den sammenkoblede telefonen. Ved tilkoblingsfeil: åpne Garmin Connect, hold Bluetooth på, og prøv manuell oppdatering.',
        },
    },
    index: {
        intro: {
            title: 'To standarder – én app',
            text: 'Atmos Air Quality kan vise enten European Air Quality Index (EAQI) eller US AQI (EPA). Tallet, fargeskalaen og kategorietikettene endres med standarden – de underliggende forurensningsmålingene er de samme.',
            switch: 'Bytt i Connect IQ-innstillingene («AQI-standard») eller via menyen på klokken.',
        },
        diff: {
            title: 'Hovedforskjeller',
            scale: 'Skala: EU har typisk lavere absolutte tall (måleren slutter rundt 120). US AQI går ofte opp til 300+ på måleren.',
            thresholds: 'Kategorigrenser varierer – samme luft kan se «moderat» ut i ett system og «unhealthy for sensitive groups» i det andre.',
            use: 'Bruk EU hvis du er vant til europeiske apper/media; bruk US hvis du sammenligner med EPA-/AirNow-rapporter.',
        },
        table: { range: 'Indeks', category: 'Kategori' },
        eu: {
            title: 'Europeisk AQI (EAQI)',
            text: 'Kombinerer flere forurensninger til én indeks. Kategorier omtrent: God → Tilfredsstillende → Moderat → Dårlig → Svært dårlig → Ekstremt dårlig.',
            c0: 'God',
            c1: 'Tilfredsstillende',
            c2: 'Moderat',
            c3: 'Dårlig',
            c4: 'Svært dårlig',
            c5: 'Ekstremt dårlig',
        },
        us: {
            title: 'US AQI (EPA)',
            text: 'Mye brukt i USA. Kategorier: Good → Moderate → Unhealthy for Sensitive Groups → Unhealthy → Very Unhealthy → Hazardous.',
            c0: 'Good',
            c1: 'Moderate',
            c2: 'Unhealthy for Sensitive Groups',
            c3: 'Unhealthy',
            c4: 'Very Unhealthy',
            c5: 'Hazardous',
        },
        warning: 'Ikke bland de to tallene. Et «80» på EU-skalaen er ikke det samme som et «80» på US-skalaen.',
    },
    values: {
        intro: {
            title: 'Hva betyr verdiene?',
            text: 'AQI er en sammendragindeks. Forurensningssidene og partikkeldigrammet viser hvilken komponent som er forhøyet. Enheter for partikler og gasser er typisk µg/m³ med mindre annet er angitt.',
        },
        pm25: {
            title: 'PM2.5',
            text: 'Fine partikler mindre enn 2,5 µm. De kan trenge dypt inn i lungene. Ofte hovedårsaken til dårlig AQI i byer og under vedrøyk / skogbrann.',
        },
        pm10: {
            title: 'PM10',
            text: 'Grovere partikler opp til 10 µm (støv, pollenfragmenter, veislitasje). Mindre dypt enn PM2.5, men fortsatt relevant for luftveisirritasjon.',
        },
        o3: {
            title: 'Ozon (O₃)',
            text: 'Ozon i bakkenivå dannes fra trafikk-/industrigasser i sollys. Ofte høyere på solrike ettermiddager; kan irritere luftveiene under utendørssport.',
        },
        no2: {
            title: 'Nitrogendioksid (NO₂)',
            text: 'Typisk knyttet til trafikk og forbrenning. Forhøyet nær travle veier; del av europeisk indeksberegning.',
        },
        so2: {
            title: 'Svoveldioksid (SO₂)',
            text: 'Fra forbrenning av svovelholdige brensler og noen industrielle kilder. Vanligvis lavere enn PM eller NO₂ i mange vest-europeiske byer.',
        },
        co: {
            title: 'Karbonmonoksid (CO)',
            text: 'Ufullstendig forbrenning (trafikk, oppvarming). Farlig ved høye innendørs konsentrasjoner; utendørsnivåer er vanligvis mye lavere.',
        },
        dust: {
            title: 'Støv',
            text: 'Mineral-/Sahara-støvbelastning. Toppverdier kan heve PM over store områder langt fra kilden.',
        },
        uv: {
            title: 'UV-indeks',
            text: 'Solbrenthetsrisiko-indeks (ikke en luftforurensning). Praktisk ved planlegging av utendørs luftkvalitet på klare dager.',
        },
        aod: {
            title: 'AOD / sikt',
            text: 'Aerosol Optical Depth beskriver hvor «disig» atmosfæren er. Appen oversetter det til omtrentlig sikt i km (klart → tett dis).',
        },
        pollen: {
            title: 'Pollen',
            text: 'Allergirelevante pollenmengder (Europa). Nivåer vises per art; diagrammet følger daglig topp på tvers av typer.',
        },
        disclaimer: 'Denne siden er veiledende, ikke medisinsk råd. For helsebeslutninger, følg lokale offisielle retningslinjer for luftkvalitet.',
    },
    settings: {
        intro: {
            title: 'Oversikt over innstillinger',
            text: 'Åpne appinnstillingene i Garmin Connect IQ (telefon) eller Garmin Express. AQI-standard kan også endres fra menyen på klokken.',
        },
        aqStd: {
            title: 'AQI-standard',
            text: 'Europeisk eller US. Påvirker tall, farger og kategorietiketter overalt (hovedvisning, diagrammer, glance, komplikasjoner).',
        },
        pages: {
            title: 'Sider 1–6',
            text: 'Hver plass kan være: Gjeldende, 24t-prognose, 5-dagers prognose, Støv, Partikler, Dis/sikt, Pollen – eller Ingen for å skjule plassen.',
            tip: 'Tips: sett Gjeldende først, deretter prognosene du bruker mest. Skjul ubrukte sider for mindre sveiping.',
        },
        series: {
            title: 'Partikkelserie A / B',
            text: 'Velg hvilke to målinger partikkelsammenligningsdiagrammet viser (PM2.5, PM10, Støv, UV, O₃, NO₂, CO, SO₂, AOD). Sett en serie til «Ingen» for å skjule den; én serie bruker hele diagrambredden.',
        },
        location: {
            title: 'Alternativ posisjon',
            text: 'Valgfri breddegrad / lengdegrad. La stå 0 / 0 for klokkens posisjon. Sett begge for å feste et fast sted (hjem, arbeid, ferie).',
        },
        interval: {
            title: 'Oppdateringsintervall',
            text: 'Hvor ofte bakgrunnstjenesten skal oppdatere (minutter). Minimum 10, maksimum 720. Standard 60. Kortere intervaller bruker mer telefondata og batteri.',
        },
        comp: {
            title: 'Komplikasjoner',
            forDevs: 'For utviklere:',
            intro: 'Denne appen publiserer komplikasjoner for bruk i urskiver',
            aqi: '"AtmosAirAQI": Gjeldende luftkvalitetsindeks (EU- eller US-indeks, avhengig av brukerens valg)',
            aqiRange: 'Inkluderer “Range” som viser hvor denne verdien faller.',
            ts: '"AtmosAirTs": UTC-tidsstempel for gjeldende verdi',
            longLabel: 'Long Label: “AtmosAirAQI”',
        },
        limits: {
            title: 'Enhetsbegrensninger (lite minne)',
            warning: 'På enkelte eldre / minnesvake modeller (f.eks. Instinct 2, Forerunner 55, Fenix 6 og lignende) er ekstra sidene Støv, Partikler, Dis og Pollen ikke tilgjengelige. Gjeldende + prognosesider forblir.',
            text: 'Dette er bevisst for at widgeten skal være stabil innenfor Garmins minnegrenser på disse enhetene.',
        },
        supportBefore: 'Spørsmål eller problemer? Bruk',
        supportLink: 'kontaktskjemaet',
        supportAfter: '.',
    },
};
export const aqproSv = {
    views: {
        intro: 'Atmos Air Quality visar aktuell luftkvalitet och prognoser som en Garmin-widget. Svep eller använd Upp/Ner för att växla mellan sidorna du aktiverat i inställningarna.',
        current: {
            title: 'Aktuell luftkvalitet',
            gauge: 'Stor färgad mätare: aktuellt AQI (EU eller US beroende på inställning). Markören visar var du befinner dig på skalan.',
            category: 'Kategorietikett (t.ex. Bra / Måttlig / Dålig) under eller bredvid värdet.',
            pollutants: 'PM2.5, PM10 och O₃ visas som snabbreferens.',
            location: 'Platsnamn och hur länge sedan data uppdaterades.',
        },
        hourly: {
            title: '24-timmars prognos',
            bars: 'Stapeldiagram över AQI för de kommande 24 timmarna, från aktuell timme.',
            colors: 'Stapelfärgerna följer samma AQI-kategorier som huvudmätaren.',
            axis: 'Tiden går vänster → höger (nu → senare).',
        },
        daily: {
            title: '5-dagars prognos',
            range: 'Varje dag visar förväntat AQI-maximum och -minimum.',
            use: 'Användbart för planering av utomhusaktiviteter de kommande dagarna.',
        },
        dust: {
            title: 'Saharadamm / damm',
            current: 'Aktuell dammnivå plus 24-timmars trend.',
            daily: 'Flerdagars dammutsikt när data finns tillgängliga.',
            note: 'Särskilt relevant under Saharadammhändelser som höjer PM-nivåer.',
        },
        particles: {
            title: 'Partikeljämförelse',
            dual: 'Dubbeldiagram som jämför två serier du väljer i inställningarna (t.ex. PM2.5 vs PM10 eller O₃ vs NO₂).',
            single: 'Om bara en serie är vald används hela bredden.',
            series: 'Tillgängliga serier: PM2.5, PM10, Damm, UV, O₃, NO₂, CO, SO₂, AOD.',
        },
        visibility: {
            title: 'Dis / sikt (AOD)',
            aod: 'Baserat på Aerosol Optical Depth (AOD) – hur mycket aerosoler dämpar atmosfären.',
            km: 'Omräknat till ungefärlig sikt i kilometer.',
            chart: '24-timmars diagram: högre staplar ≈ bättre sikt; färg visar disstyrka.',
        },
        pollen: {
            title: 'Pollen (Europa)',
            species: 'Sex typer: al, björk, gräs, malört, oliv, ambrosia – var och en med nivåstapel.',
            chart: 'Nedersta diagram: 24-timmars maximum över alla typer.',
            europe: 'Data kommer från CAMS och är vanligtvis bara tillgänglig i Europa. Utanför Europa kan du se «Ingen data».',
        },
        glance: {
            title: 'Glance-vy',
            what: 'Kompakt urtavla-glance: aktuellt AQI-värde, kategori och färgskala med markör.',
            mono: 'På monokroma Instinct-klockor används en förloppsindikator i stället för färgskalan.',
        },
    },
    controls: {
        nav: {
            title: 'Navigera mellan sidor',
            swipe: 'Pekskärm: svep vänster/höger (eller upp/ner beroende på klockan) för att byta sida.',
            buttons: 'Knappsklockor: Upp / Ner eller Nästa / Föregående sida.',
            order: 'Bara sidor du tilldelat i inställningarna (Sida 1–6) visas. Tomma platser («Ingen») hoppas över.',
        },
        refresh: {
            title: 'Manuell uppdatering',
            text: 'Tryck Select / Enter / Start för att begära nya data direkt. Klockan behöver telefonanslutning (Bluetooth) för nedladdningen.',
            bg: 'I bakgrunden uppdaterar appen också automatiskt med intervallet från inställningarna (standard 60 minuter).',
        },
        menu: {
            title: 'Meny på klockan (AQI-standard)',
            text: 'Öppna menyn på klockan för att växla mellan europeiskt AQI och US AQI. Skriver samma inställning som i Connect IQ Mobile – den senaste ändringen gäller.',
        },
        location: {
            title: 'Plats',
            text: 'Som standard använder appen klockans/telefonens plats. Du kan åsidosätta med alternativa koordinater i inställningarna (användbart om du vill ha luftkvalitet för en annan plats).',
            gps: 'Om platsen saknas eller är inaktuell: få GPS-fix (starta en aktivitet kort eller använd GPS-widget), uppdatera sedan.',
        },
        connection: {
            title: 'Telefonanslutning',
            text: 'Internetförfrågningar går via den parkopplade telefonen. Vid anslutningsfel: öppna Garmin Connect, håll Bluetooth på och prova manuell uppdatering.',
        },
    },
    index: {
        intro: {
            title: 'Två standarder – en app',
            text: 'Atmos Air Quality kan visa antingen European Air Quality Index (EAQI) eller US AQI (EPA). Siffran, färgskalan och kategorietiketterna ändras med standarden – de underliggande föroreningsmätningarna är desamma.',
            switch: 'Växla i Connect IQ-inställningarna («AQI-standard») eller via menyn på klockan.',
        },
        diff: {
            title: 'Huvudsakliga skillnader',
            scale: 'Skala: EU har typiskt lägre absoluta tal (mätaren slutar runt 120). US AQI går ofta upp till 300+ på mätaren.',
            thresholds: 'Kategorigränser skiljer sig – samma luft kan se «måttlig» ut i ett system och «unhealthy for sensitive groups» i det andra.',
            use: 'Använd EU om du är van vid europeiska appar/media; använd US om du jämför med EPA-/AirNow-rapporter.',
        },
        table: { range: 'Index', category: 'Kategori' },
        eu: {
            title: 'Europeiskt AQI (EAQI)',
            text: 'Kombinerar flera föroreningar till ett index. Kategorier ungefär: Bra → Tillfredsställande → Måttlig → Dålig → Mycket dålig → Extremt dålig.',
            c0: 'Bra',
            c1: 'Tillfredsställande',
            c2: 'Måttlig',
            c3: 'Dålig',
            c4: 'Mycket dålig',
            c5: 'Extremt dålig',
        },
        us: {
            title: 'US AQI (EPA)',
            text: 'Mycket använt i USA. Kategorier: Good → Moderate → Unhealthy for Sensitive Groups → Unhealthy → Very Unhealthy → Hazardous.',
            c0: 'Good',
            c1: 'Moderate',
            c2: 'Unhealthy for Sensitive Groups',
            c3: 'Unhealthy',
            c4: 'Very Unhealthy',
            c5: 'Hazardous',
        },
        warning: 'Blanda inte de två siffrorna. Ett «80» på EU-skalan är inte samma sak som ett «80» på US-skalan.',
    },
    values: {
        intro: {
            title: 'Vad betyder värdena?',
            text: 'AQI är ett sammanfattande index. Föroreningssidorna och partikeldigrammet visar vilken komponent som är förhöjd. Enheter för partiklar och gaser är typiskt µg/m³ om inget annat anges.',
        },
        pm25: {
            title: 'PM2.5',
            text: 'Fina partiklar mindre än 2,5 µm. De kan tränga djupt in i lungorna. Ofta huvudorsaken till dåligt AQI i städer och under vedrök / skogsbrand.',
        },
        pm10: {
            title: 'PM10',
            text: 'Grövre partiklar upp till 10 µm (damm, pollenfragment, vägslitage). Mindre djupt än PM2.5, men fortfarande relevant för luftvägsirritation.',
        },
        o3: {
            title: 'Ozon (O₃)',
            text: 'Ozon i marknivå bildas från trafik-/industrigas i solljus. Ofta högre på soliga eftermiddagar; kan irritera luftvägarna vid utomhusidrott.',
        },
        no2: {
            title: 'Kvävedioxid (NO₂)',
            text: 'Typiskt kopplat till trafik och förbränning. Förhöjt nära trafikerade vägar; del av europeisk indexberäkning.',
        },
        so2: {
            title: 'Svaveldioxid (SO₂)',
            text: 'Från förbränning av svavelhaltiga bränslen och vissa industriella källor. Vanligtvis lägre än PM eller NO₂ i många västeuropeiska städer.',
        },
        co: {
            title: 'Kolmonoxid (CO)',
            text: 'Ofullständig förbränning (trafik, uppvärmning). Farligt vid höga inomhuskoncentrationer; utomhusnivåer är vanligtvis mycket lägre.',
        },
        dust: {
            title: 'Damm',
            text: 'Mineral-/Saharadammbelastning. Toppvärden kan höja PM över stora områden långt från källan.',
        },
        uv: {
            title: 'UV-index',
            text: 'Solbränneriskindex (inte en luftförorening). Praktiskt vid planering av utomhus luftkvalitet på klara dagar.',
        },
        aod: {
            title: 'AOD / sikt',
            text: 'Aerosol Optical Depth beskriver hur «disig» atmosfären är. Appen mappar det till ungefärlig sikt i km (klart → tät dis).',
        },
        pollen: {
            title: 'Pollen',
            text: 'Allergirelevanta pollenmängder (Europa). Nivåer visas per art; diagrammet följer daglig topp över typer.',
        },
        disclaimer: 'Denna sida är vägledande, inte medicinsk rådgivning. För hälsobeslut, följ lokala officiella riktlinjer för luftkvalitet.',
    },
    settings: {
        intro: {
            title: 'Översikt av inställningar',
            text: 'Öppna appinställningarna i Garmin Connect IQ (telefon) eller Garmin Express. AQI-standard kan också ändras från menyn på klockan.',
        },
        aqStd: {
            title: 'AQI-standard',
            text: 'Europeisk eller US. Påverkar siffra, färger och kategorietiketter överallt (huvudvy, diagram, glance, komplikationer).',
        },
        pages: {
            title: 'Sidor 1–6',
            text: 'Varje plats kan vara: Aktuell, 24h-prognos, 5-dagars prognos, Damm, Partiklar, Dis/sikt, Pollen – eller Ingen för att dölja platsen.',
            tip: 'Tips: sätt Aktuell först, sedan prognoserna du använder mest. Dölj oanvända sidor för mindre svepande.',
        },
        series: {
            title: 'Partikelserie A / B',
            text: 'Välj vilka två mått partikeljämförelsediagrammet visar (PM2.5, PM10, Damm, UV, O₃, NO₂, CO, SO₂, AOD). Sätt en serie till «Ingen» för att dölja den; en serie använder hela diagrambredden.',
        },
        location: {
            title: 'Alternativ plats',
            text: 'Valfri latitud / longitud. Lämna 0 / 0 för klockans plats. Sätt båda för att fästa en fast plats (hem, arbete, semester).',
        },
        interval: {
            title: 'Uppdateringsintervall',
            text: 'Hur ofta bakgrundstjänsten ska uppdatera (minuter). Minimum 10, maximum 720. Standard 60. Kortare intervall använder mer telefondata och batteri.',
        },
        comp: {
            title: 'Komplikationer',
            forDevs: 'För utvecklare:',
            intro: 'Den här appen publicerar komplikationer för användning i urtavlor',
            aqi: '"AtmosAirAQI": Aktuellt luftkvalitetsindex (EU- eller US-index beroende på användarens val)',
            aqiRange: 'Inkluderar “Range” som visar var detta värde hamnar.',
            ts: '"AtmosAirTs": UTC-tidsstämpel för aktuellt värde',
            longLabel: 'Long Label: “AtmosAirAQI”',
        },
        limits: {
            title: 'Enhetsbegränsningar (lite minne)',
            warning: 'På vissa äldre / minnessvaga modeller (t.ex. Instinct 2, Forerunner 55, Fenix 6 och liknande) är extrasidorna Damm, Partiklar, Dis och Pollen inte tillgängliga. Aktuell + prognossidor finns kvar.',
            text: 'Detta är avsiktligt så att widgeten förblir stabil inom Garmins minnesgränser på dessa enheter.',
        },
        supportBefore: 'Frågor eller problem? Använd',
        supportLink: 'kontaktformuläret',
        supportAfter: '.',
    },
};
export const aqproJa = {
    views: {
        intro: 'Atmos Air Quality は Garmin ウィジェットとして現在の大気質と予報を表示します。スワイプまたは上下ボタンで、設定で有効にしたページ間を移動できます。',
        current: {
            title: '現在の大気質',
            gauge: '大きなカラーゲージ：現在の AQI（設定に応じて EU または US）。マーカーがスケール上の位置を示します。',
            category: '値の下または横にカテゴリーラベル（例：良好 / 中程度 / 悪い）。',
            pollutants: 'PM2.5、PM10、O₃ の値をクイックリファレンスとして表示。',
            location: '場所名とデータの最終更新からの経過時間。',
        },
        hourly: {
            title: '24時間予報',
            bars: '現在の時刻から次の24時間の AQI 棒グラフ。',
            colors: '棒の色はメインゲージと同じ AQI カテゴリーに従います。',
            axis: '時間は左 → 右（現在 → 後）に進みます。',
        },
        daily: {
            title: '5日間予報',
            range: '各日に AQI の予想最大値と最小値を表示。',
            use: '今後数日の屋外活動の計画に便利です。',
        },
        dust: {
            title: 'サハラ砂塵 / 砂塵',
            current: '現在の砂塵レベルと24時間トレンド。',
            daily: 'データが利用可能な場合の複数日の砂塵見通し。',
            note: 'PM レベルを上げるサハラ砂塵イベント時に特に重要。',
        },
        particles: {
            title: '粒子比較',
            dual: '設定で選択した2つの系列を比較するデュアルチャート（例：PM2.5 vs PM10、O₃ vs NO₂）。',
            single: '1つの系列のみ選択した場合、全幅を使用します。',
            series: '利用可能な系列：PM2.5、PM10、砂塵、UV、O₃、NO₂、CO、SO₂、AOD。',
        },
        visibility: {
            title: 'もや / 視程 (AOD)',
            aod: 'Aerosol Optical Depth (AOD) に基づく – エアロゾルが大気をどれだけ曇らせるか。',
            km: 'おおよその視程（キロメートル）に変換。',
            chart: '24時間チャート：棒が高い ≈ 視程良好；色はもやの濃さを示します。',
        },
        pollen: {
            title: '花粉（ヨーロッパ）',
            species: '6種類：ハンノキ、シラカバ、イネ科、ヨモギ、オリーブ、アレルギー草 – 各レベルバー付き。',
            chart: '下部チャート：全種類の24時間最大値。',
            europe: 'データは CAMS から取得され、通常ヨーロッパでのみ利用可能。ヨーロッパ外では「データなし」と表示される場合があります。',
        },
        glance: {
            title: 'Glance ビュー',
            what: 'コンパクトな文字盤 Glance：現在の AQI 値、カテゴリー、マーカー付きカラースケール。',
            mono: 'モノクロ Instinct ウォッチではカラースケールの代わりにプログレスバーを使用。',
        },
    },
    controls: {
        nav: {
            title: 'ページ間の移動',
            swipe: 'タッチスクリーン：左/右（またはウォッチにより上/下）にスワイプしてページを変更。',
            buttons: 'ボタン式ウォッチ：上 / 下 または 次のページ / 前のページ。',
            order: '設定で割り当てたページ（ページ 1–6）のみ表示。「なし」の空きスロットはスキップされます。',
        },
        refresh: {
            title: '手動更新',
            text: 'Select / Enter / Start を押して即座に新しいデータを要求。ダウンロードには電話接続（Bluetooth）が必要です。',
            bg: 'バックグラウンドでも設定の間隔（デフォルト60分）で自動更新されます。',
        },
        menu: {
            title: 'ウォッチ上のメニュー（AQI 標準）',
            text: 'ウォッチのメニューを開いて European AQI と US AQI を切り替え。Connect IQ Mobile と同じ設定を書き込み – 最後に変更した方が有効。',
        },
        location: {
            title: '位置',
            text: 'デフォルトではウォッチ/電話の位置を使用。設定で代替座標を指定可能（別の場所の大気質を見たい場合に便利）。',
            gps: '位置がないか古い場合：GPS 測位を取得（短時間アクティビティを開始するか GPS ウィジェットを使用）、その後更新。',
        },
        connection: {
            title: '電話接続',
            text: 'インターネット要求はペアリングされた電話経由。接続エラーが表示された場合：Garmin Connect を開き、Bluetooth をオンにして手動更新を試してください。',
        },
    },
    index: {
        intro: {
            title: '2つの標準 – 1つのアプリ',
            text: 'Atmos Air Quality は European Air Quality Index (EAQI) または US AQI (EPA) を表示できます。数値、カラースケール、カテゴリーラベルは標準に応じて変わります – 基礎となる汚染物質の測定値は同じです。',
            switch: 'Connect IQ 設定（「AQI 標準」）またはウォッチ上のメニューで切り替え。',
        },
        diff: {
            title: '主な違い',
            scale: 'スケール：EU は通常より低い絶対値（ゲージは約120で終了）。US AQI はゲージ上で300+まで一般的。',
            thresholds: 'カテゴリー閾値が異なります – 同じ空気でも一方では「中程度」、他方では「unhealthy for sensitive groups」と見える場合があります。',
            use: 'ヨーロッパのアプリ/メディアに慣れている場合は EU；US EPA / AirNow 形式の報告と比較する場合は US を使用。',
        },
        table: { range: '指数', category: 'カテゴリー' },
        eu: {
            title: 'European AQI (EAQI)',
            text: '複数の汚染物質を1つの指数に統合。カテゴリー概ね：良好 → 普通 → 中程度 → 悪い → 非常に悪い → 極めて悪い。',
            c0: '良好',
            c1: '普通',
            c2: '中程度',
            c3: '悪い',
            c4: '非常に悪い',
            c5: '極めて悪い',
        },
        us: {
            title: 'US AQI (EPA)',
            text: '米国で広く使用。カテゴリー：Good → Moderate → Unhealthy for Sensitive Groups → Unhealthy → Very Unhealthy → Hazardous.',
            c0: 'Good',
            c1: 'Moderate',
            c2: 'Unhealthy for Sensitive Groups',
            c3: 'Unhealthy',
            c4: 'Very Unhealthy',
            c5: 'Hazardous',
        },
        warning: '2つの数値を混同しないでください。EU スケールの「80」は US スケールの「80」とは同じではありません。',
    },
    values: {
        intro: {
            title: '値の意味は？',
            text: 'AQI は要約指数です。汚染物質ページと粒子チャートで、どの成分が高いか確認できます。粒子とガスの単位は通常 µg/m³ です（特に記載がない限り）。',
        },
        pm25: {
            title: 'PM2.5',
            text: '2.5 µm 未満の微粒子。肺の深部まで到達可能。都市や薪の煙 / 野火時の悪い AQI の主因となることが多い。',
        },
        pm10: {
            title: 'PM10',
            text: '10 µm までの粗い粒子（砂塵、花粉断片、道路摩耗）。PM2.5 ほど深くは入らないが、呼吸器刺激には依然として重要。',
        },
        o3: {
            title: 'オゾン (O₃)',
            text: '地表オゾンは日光下で交通/工業ガスから形成。晴れた午後に高くなりがち；屋外スポーツ中に気道を刺激する可能性。',
        },
        no2: {
            title: '二酸化窒素 (NO₂)',
            text: '通常交通と燃焼に関連。繁忙道路付近で高値；European 指数計算の一部。',
        },
        so2: {
            title: '二酸化硫黄 (SO₂)',
            text: '硫黄含有燃料の燃焼と一部の工業源から。多くの西ヨーロッパ都市では PM や NO₂ より低いことが多い。',
        },
        co: {
            title: '一酸化炭素 (CO)',
            text: '不完全燃焼（交通、暖房）。室内で高濃度は危険；屋外レベルは通常はるかに低い。',
        },
        dust: {
            title: '砂塵',
            text: '鉱物 / サハラ砂塵負荷。スパイクは発源地から遠く離れた広域で PM 値を上げる可能性。',
        },
        uv: {
            title: 'UV 指数',
            text: '日焼けリスク指数（大気汚染物質ではない）。晴れた日の屋外大気質計画に便利。',
        },
        aod: {
            title: 'AOD / 視程',
            text: 'Aerosol Optical Depth は大気の「もや」の程度を示す。アプリはおおよその視程（km）にマッピング（晴れ → 濃いもや）。',
        },
        pollen: {
            title: '花粉',
            text: 'アレルギー関連の花粉量（ヨーロッパ）。種ごとにレベル表示；チャートは種類間の日最大値を追跡。',
        },
        disclaimer: 'このページは教育的な内容であり、医学的助言ではありません。健康に関する判断は、地域の公式大気質ガイダンスに従ってください。',
    },
    settings: {
        intro: {
            title: '設定の概要',
            text: 'Garmin Connect IQ（電話）または Garmin Express でアプリ設定を開く。AQI 標準はウォッチ上のメニューからも変更可能。',
        },
        aqStd: {
            title: 'AQI 標準',
            text: 'European または US。数値、色、カテゴリーラベル全体に影響（メインビュー、チャート、Glance、コンプリケーション）。',
        },
        pages: {
            title: 'ページ 1–6',
            text: '各スロット：現在、24時間予報、5日間予報、砂塵、粒子、もや/視程、花粉 – または非表示の「なし」。',
            tip: 'ヒント：現在を最初に、よく使う予報を続けて配置。未使用ページを非表示にしてスワイプを減らす。',
        },
        series: {
            title: '粒子系列 A / B',
            text: '粒子比較チャートに表示する2つの指標を選択（PM2.5、PM10、砂塵、UV、O₃、NO₂、CO、SO₂、AOD）。系列を「なし」に設定で非表示；1系列は全幅使用。',
        },
        location: {
            title: '代替位置',
            text: '任意の緯度 / 経度。0 / 0 のままにするとウォッチの位置を使用。両方設定で固定場所（自宅、職場、休暇先）を指定。',
        },
        interval: {
            title: '更新間隔',
            text: 'バックグラウンドサービスの更新頻度（分）。最小10、最大720。デフォルト60。短い間隔は電話データとバッテリーを多く消費。',
        },
        comp: {
            title: 'コンプリケーション',
            forDevs: '開発者向け:',
            intro: 'このアプリは文字盤で使えるコンプリケーションを公開します',
            aqi: '"AtmosAirAQI": 現在の大気質指数（ユーザー選択に応じて EU または US 指数）',
            aqiRange: '値の位置を示す “Range” を含みます。',
            ts: '"AtmosAirTs": 現在値の UTC タイムスタンプ',
            longLabel: 'Long Label: “AtmosAirAQI”',
        },
        limits: {
            title: 'デバイス制限（低メモリ）',
            warning: '一部の旧式 / 低メモリモデル（例：Instinct 2、Forerunner 55、Fenix 6 など）では追加ページ（砂塵、粒子、もや、花粉）は利用不可。現在 + 予報ページは残ります。',
            text: 'これは Garmin のメモリ制限内でウィジェットを安定させるための意図的な設計です。',
        },
        supportBefore: 'ご質問や問題がありますか？',
        supportLink: 'お問い合わせフォーム',
        supportAfter: 'をご利用ください。',
    },
};
export const aqproKo = {
    views: {
        intro: 'Atmos Air Quality는 Garmin 위젯으로 현재 대기질과 예보를 표시합니다. 스와이프하거나 위/아래 버튼으로 설정에서 활성화한 페이지 간 이동할 수 있습니다.',
        current: {
            title: '현재 대기질',
            gauge: '큰 컬러 게이지: 현재 AQI(설정에 따라 EU 또는 US). 마커가 눈금 위치를 표시합니다.',
            category: '값 아래 또는 옆에 카테고리 라벨(예: 좋음 / 보통 / 나쁨).',
            pollutants: 'PM2.5, PM10, O₃ 값을 빠른 참조로 표시.',
            location: '위치 이름과 데이터 마지막 업데이트 경과 시간.',
        },
        hourly: {
            title: '24시간 예보',
            bars: '현재 시각부터 다음 24시간 AQI 막대 그래프.',
            colors: '막대 색상은 메인 게이지와 동일한 AQI 카테고리를 따릅니다.',
            axis: '시간은 왼쪽 → 오른쪽(지금 → 이후)으로 진행.',
        },
        daily: {
            title: '5일 예보',
            range: '각 날짜에 예상 AQI 최대값과 최소값 표시.',
            use: '앞으로 며칠간 야외 활동 계획에 유용.',
        },
        dust: {
            title: '사하라 먼지 / 먼지',
            current: '현재 먼지 수준과 24시간 추세.',
            daily: '데이터가 있을 때 다일 먼지 전망.',
            note: 'PM 수준을 높이는 사하라 먼지 사건 시 특히 중요.',
        },
        particles: {
            title: '입자 비교',
            dual: '설정에서 선택한 두 계열을 비교하는 이중 차트(예: PM2.5 vs PM10, O₃ vs NO₂).',
            single: '하나의 계열만 선택하면 전체 너비 사용.',
            series: '사용 가능한 계열: PM2.5, PM10, 먼지, UV, O₃, NO₂, CO, SO₂, AOD.',
        },
        visibility: {
            title: '연무 / 가시거리 (AOD)',
            aod: 'Aerosol Optical Depth (AOD) 기반 – 에어로졸이 대기를 얼마나 흐리게 하는지.',
            km: '대략적인 가시거리(킬로미터)로 변환.',
            chart: '24시간 차트: 막대가 높을수록 ≈ 가시거리 좋음; 색상은 연무 밀도 표시.',
        },
        pollen: {
            title: '꽃가루 (유럽)',
            species: '6종: 오리나무, 자작나무, 풀, 쑥, 올리브, 돼지풀 – 각 수준 막대.',
            chart: '하단 차트: 모든 종의 24시간 최대값.',
            europe: '데이터는 CAMS에서 제공되며 일반적으로 유럽에서만 이용 가능. 유럽 밖에서는 «데이터 없음»이 표시될 수 있습니다.',
        },
        glance: {
            title: 'Glance 보기',
            what: '컴팩트 워치페이스 Glance: 현재 AQI 값, 카테고리, 마커가 있는 색상 눈금.',
            mono: '모노크롬 Instinct 시계에서는 색상 눈금 대신 진행률 막대 사용.',
        },
    },
    controls: {
        nav: {
            title: '페이지 간 이동',
            swipe: '터치스크린: 좌/우(또는 시계에 따라 상/하) 스와이프로 페이지 변경.',
            buttons: '버튼 시계: 위 / 아래 또는 다음 / 이전 페이지.',
            order: '설정에서 지정한 페이지(페이지 1–6)만 표시. 빈 슬롯(«없음»)은 건너뜀.',
        },
        refresh: {
            title: '수동 새로고침',
            text: 'Select / Enter / Start를 눌러 즉시 새 데이터 요청. 다운로드에는 전화 연결(Bluetooth) 필요.',
            bg: '백그라운드에서도 설정 간격(기본 60분)으로 자동 업데이트.',
        },
        menu: {
            title: '시계 메뉴 (AQI 표준)',
            text: '시계 메뉴를 열어 European AQI와 US AQI 전환. Connect IQ Mobile과 동일 설정 기록 – 마지막 변경이 적용.',
        },
        location: {
            title: '위치',
            text: '기본적으로 시계/전화 위치 사용. 설정에서 대체 좌표 지정 가능(다른 장소의 대기질 확인 시 유용).',
            gps: '위치가 없거나 오래된 경우: GPS 고정(짧게 활동 시작 또는 GPS 위젯 사용) 후 새로고침.',
        },
        connection: {
            title: '전화 연결',
            text: '인터넷 요청은 페어링된 전화 경유. 연결 오류 시: Garmin Connect 열기, Bluetooth 켜기, 수동 새로고침 시도.',
        },
    },
    index: {
        intro: {
            title: '두 표준 – 하나의 앱',
            text: 'Atmos Air Quality는 European Air Quality Index (EAQI) 또는 US AQI (EPA)를 표시할 수 있습니다. 숫자, 색상 눈금, 카테고리 라벨은 표준에 따라 바뀌지만 – 기초 오염물질 측정값은 동일합니다.',
            switch: 'Connect IQ 설정(«AQI 표준») 또는 시계 메뉴에서 전환.',
        },
        diff: {
            title: '주요 차이',
            scale: '눈금: EU는 일반적으로 더 낮은 절대값(게이지는 약 120에서 종료). US AQI는 게이지에서 300+까지 일반적.',
            thresholds: '카테고리 임계값이 다름 – 같은 공기가 한 시스템에서는 «보통», 다른 시스템에서는 «unhealthy for sensitive groups»로 보일 수 있음.',
            use: '유럽 앱/미디어에 익숙하면 EU; US EPA / AirNow 보고서와 비교하면 US 사용.',
        },
        table: { range: '지수', category: '카테고리' },
        eu: {
            title: 'European AQI (EAQI)',
            text: '여러 오염물질을 하나의 지수로 통합. 카테고리 대략: 좋음 → 보통 → 중간 → 나쁨 → 매우 나쁨 → 극도로 나쁨.',
            c0: '좋음',
            c1: '보통',
            c2: '중간',
            c3: '나쁨',
            c4: '매우 나쁨',
            c5: '극도로 나쁨',
        },
        us: {
            title: 'US AQI (EPA)',
            text: '미국에서 널리 사용. 카테고리: Good → Moderate → Unhealthy for Sensitive Groups → Unhealthy → Very Unhealthy → Hazardous.',
            c0: 'Good',
            c1: 'Moderate',
            c2: 'Unhealthy for Sensitive Groups',
            c3: 'Unhealthy',
            c4: 'Very Unhealthy',
            c5: 'Hazardous',
        },
        warning: '두 숫자를 혼동하지 마세요. EU 눈금의 «80»은 US 눈금의 «80»과 같지 않습니다.',
    },
    values: {
        intro: {
            title: '값의 의미는?',
            text: 'AQI는 요약 지수입니다. 오염물질 페이지와 입자 차트로 어떤 성분이 높은지 확인. 입자와 가스 단위는 일반적으로 µg/m³(별도 표기 없을 때).',
        },
        pm25: {
            title: 'PM2.5',
            text: '2.5 µm 미만 미세입자. 폐 깊숙이 침투 가능. 도시 및 장작 연기 / 산불 시 나쁜 AQI의 주요 원인.',
        },
        pm10: {
            title: 'PM10',
            text: '10 µm까지 거친 입자(먼지, 꽃가루 조각, 도로 마모). PM2.5만큼 깊지 않지만 호흡기 자극에 여전히 중요.',
        },
        o3: {
            title: '오존 (O₃)',
            text: '지표 오존은 햇빛 아래 교통/산업 가스에서 형성. 맑은 오후에 높아지기 쉬움; 야외 운동 중 기도 자극 가능.',
        },
        no2: {
            title: '이산화질소 (NO₂)',
            text: '일반적으로 교통과 연소 관련. 번잡한 도로 근처에서 높음; European 지수 계산의 일부.',
        },
        so2: {
            title: '이산화황 (SO₂)',
            text: '황 함유 연료 연소 및 일부 산업원. 많은 서유럽 도시에서 PM이나 NO₂보다 낮은 경우가 많음.',
        },
        co: {
            title: '일산화탄소 (CO)',
            text: '불완전 연소(교통, 난방). 실내 고농도는 위험; 실외 수준은 보통 훨씬 낮음.',
        },
        dust: {
            title: '먼지',
            text: '광물 / 사하라 먼지 부하. 급증은 발원지에서 멀리 떨어진 넓은 지역에서 PM 값을 높일 수 있음.',
        },
        uv: {
            title: 'UV 지수',
            text: '일광화상 위험 지수(대기 오염물질 아님). 맑은 날 야외 대기질 계획에 유용.',
        },
        aod: {
            title: 'AOD / 가시거리',
            text: 'Aerosol Optical Depth는 대기 «연무» 정도를 설명. 앱은 대략적 가시거리(km)로 매핑(맑음 → 짙은 연무).',
        },
        pollen: {
            title: '꽃가루',
            text: '알레르기 관련 꽃가루량(유럽). 종별 수준 표시; 차트는 종류 간 일일 최고치 추적.',
        },
        disclaimer: '이 페이지는 교육 목적이며 의학적 조언이 아닙니다. 건강 결정은 지역 공식 대기질 지침을 따르세요.',
    },
    settings: {
        intro: {
            title: '설정 개요',
            text: 'Garmin Connect IQ(전화) 또는 Garmin Express에서 앱 설정 열기. AQI 표준은 시계 메뉴에서도 변경 가능.',
        },
        aqStd: {
            title: 'AQI 표준',
            text: 'European 또는 US. 숫자, 색상, 카테고리 라벨 전체에 영향(메인 보기, 차트, Glance, 컴플리케이션).',
        },
        pages: {
            title: '페이지 1–6',
            text: '각 슬롯: 현재, 24시간 예보, 5일 예보, 먼지, 입자, 연무/가시거리, 꽃가루 – 또는 «없음»으로 숨김.',
            tip: '팁: 현재를 먼저, 자주 쓰는 예보를 다음에 배치. 미사용 페이지 숨겨 스와이프 줄이기.',
        },
        series: {
            title: '입자 계열 A / B',
            text: '입자 비교 차트에 표시할 두 지표 선택(PM2.5, PM10, 먼지, UV, O₃, NO₂, CO, SO₂, AOD). 계열을 «없음»으로 설정해 숨김; 하나의 계열은 전체 너비 사용.',
        },
        location: {
            title: '대체 위치',
            text: '선택적 위도 / 경도. 0 / 0이면 시계 위치 사용. 둘 다 설정하면 고정 장소(집, 직장, 휴가지) 지정.',
        },
        interval: {
            title: '업데이트 간격',
            text: '백그라운드 서비스 새로고침 주기(분). 최소 10, 최대 720. 기본 60. 짧은 간격은 전화 데이터와 배터리 더 사용.',
        },
        comp: {
            title: '컴플리케이션',
            forDevs: '개발자용:',
            intro: '이 앱은 워치페이스에서 사용할 컴플리케이션을 게시합니다',
            aqi: '"AtmosAirAQI": 현재 대기질 지수(사용자 선택에 따라 EU 또는 US 지수)',
            aqiRange: '값이 어디에 해당하는지 나타내는 “Range”를 포함합니다.',
            ts: '"AtmosAirTs": 현재 값의 UTC 타임스탬프',
            longLabel: 'Long Label: “AtmosAirAQI”',
        },
        limits: {
            title: '기기 제한 (저메모리)',
            warning: '일부 구형 / 저메모리 모델(예: Instinct 2, Forerunner 55, Fenix 6 등)에서는 추가 페이지(먼지, 입자, 연무, 꽃가루)를 사용할 수 없음. 현재 + 예보 페이지는 유지.',
            text: 'Garmin 메모리 한도 내에서 위젯 안정성을 위해 의도적으로 설계됨.',
        },
        supportBefore: '질문이나 문제가 있으신가요?',
        supportLink: '문의 양식',
        supportAfter: '을 이용해 주세요.',
    },
};
export const aqproZh = {
    views: {
        intro: 'Atmos Air Quality 以 Garmin 小组件显示当前空气质量与预报。滑动或使用上/下键在设置中启用的页面之间切换。',
        current: {
            title: '当前空气质量',
            gauge: '大型彩色仪表：当前 AQI（根据设置显示 EU 或 US）。标记显示您在刻度上的位置。',
            category: '数值下方或旁边的类别标签（如：优 / 中等 / 差）。',
            pollutants: 'PM2.5、PM10 和 O₃ 值作为快速参考显示。',
            location: '地点名称及数据更新时间。',
        },
        hourly: {
            title: '24 小时预报',
            bars: '从当前小时起未来 24 小时的 AQI 柱状图。',
            colors: '柱形颜色与主仪表使用相同的 AQI 类别。',
            axis: '时间从左 → 右（现在 → 稍后）。',
        },
        daily: {
            title: '5 天预报',
            range: '每天显示预期的 AQI 最高值和最低值。',
            use: '便于规划未来几天的户外活动。',
        },
        dust: {
            title: '撒哈拉沙尘 / 沙尘',
            current: '当前沙尘水平加 24 小时趋势。',
            daily: '有数据时的多日沙尘展望。',
            note: '在撒哈拉沙尘事件抬高 PM 水平时尤其相关。',
        },
        particles: {
            title: '颗粒物对比',
            dual: '双图表对比设置中选择的两组数据（如 PM2.5 vs PM10 或 O₃ vs NO₂）。',
            single: '若只选择一组数据，则使用全宽显示。',
            series: '可用系列：PM2.5、PM10、沙尘、UV、O₃、NO₂、CO、SO₂、AOD。',
        },
        visibility: {
            title: '霾 / 能见度 (AOD)',
            aod: '基于 Aerosol Optical Depth (AOD) – 气溶胶使大气变暗的程度。',
            km: '转换为大约的能见度（公里）。',
            chart: '24 小时图表：柱越高 ≈ 能见度越好；颜色表示霾的密度。',
        },
        pollen: {
            title: '花粉（欧洲）',
            species: '六种：桤木、桦木、草、艾蒿、橄榄、豚草 – 各带水平条。',
            chart: '底部图表：所有类型的 24 小时最大值。',
            europe: '数据来自 CAMS，通常仅在欧洲可用。欧洲以外可能显示「无数据」。',
        },
        glance: {
            title: 'Glance 视图',
            what: '紧凑表盘 Glance：当前 AQI 值、类别及带标记的色标。',
            mono: '单色 Instinct 手表使用进度条代替色标。',
        },
    },
    controls: {
        nav: {
            title: '页面间导航',
            swipe: '触摸屏：左/右滑动（或根据手表上/下）切换页面。',
            buttons: '按键手表：上 / 下 或 下一页 / 上一页。',
            order: '仅显示设置中分配的页面（页面 1–6）。空位（「无」）会跳过。',
        },
        refresh: {
            title: '手动刷新',
            text: '按 Select / Enter / Start 立即请求新数据。下载需要手机连接（Bluetooth）。',
            bg: '后台也会按设置间隔（默认 60 分钟）自动更新。',
        },
        menu: {
            title: '手表菜单（AQI 标准）',
            text: '在手表上打开菜单切换 European AQI 与 US AQI。写入与 Connect IQ Mobile 相同的设置 – 以最后更改为准。',
        },
        location: {
            title: '位置',
            text: '默认使用手表/手机位置。可在设置中覆盖为其他坐标（查看其他地点空气质量时有用）。',
            gps: '若位置缺失或过时：获取 GPS 定位（短暂开始活动或使用 GPS 小组件），然后刷新。',
        },
        connection: {
            title: '手机连接',
            text: '互联网请求通过已配对手机。若出现连接错误：打开 Garmin Connect，保持 Bluetooth 开启，尝试手动刷新。',
        },
    },
    index: {
        intro: {
            title: '两种标准 – 一个应用',
            text: 'Atmos Air Quality 可显示 European Air Quality Index (EAQI) 或 US AQI (EPA)。数值、色标和类别标签随标准变化 – 底层污染物测量值相同。',
            switch: '在 Connect IQ 设置（「AQI 标准」）或手表菜单中切换。',
        },
        diff: {
            title: '主要差异',
            scale: '刻度：EU 通常绝对值较低（仪表约到 120）。US AQI 在仪表上常见到 300+。',
            thresholds: '类别阈值不同 – 同一空气在一个系统中可能为「中等」，在另一个系统中为「unhealthy for sensitive groups」。',
            use: '若习惯欧洲应用/媒体请用 EU；若与 US EPA / AirNow 报告对比请用 US。',
        },
        table: { range: '指数', category: '类别' },
        eu: {
            title: 'European AQI (EAQI)',
            text: '将多种污染物合并为一个指数。类别大致：优 → 良 → 中 → 差 → 很差 → 极差。',
            c0: '优',
            c1: '良',
            c2: '中',
            c3: '差',
            c4: '很差',
            c5: '极差',
        },
        us: {
            title: 'US AQI (EPA)',
            text: '在美国广泛使用。类别：Good → Moderate → Unhealthy for Sensitive Groups → Unhealthy → Very Unhealthy → Hazardous.',
            c0: 'Good',
            c1: 'Moderate',
            c2: 'Unhealthy for Sensitive Groups',
            c3: 'Unhealthy',
            c4: 'Very Unhealthy',
            c5: 'Hazardous',
        },
        warning: '不要混淆两个数值。EU 刻度上的「80」与 US 刻度上的「80」并不相同。',
    },
    values: {
        intro: {
            title: '数值含义？',
            text: 'AQI 是汇总指数。污染物页面和颗粒物图表可查看哪项成分偏高。颗粒物和气体单位通常为 µg/m³（除非另有说明）。',
        },
        pm25: {
            title: 'PM2.5',
            text: '小于 2.5 µm 的细颗粒物。可深入肺部。常是城市和木烟 / 野火期间 AQI 变差的主因。',
        },
        pm10: {
            title: 'PM10',
            text: '最大 10 µm 的较粗颗粒物（灰尘、花粉碎片、道路磨损）。不如 PM2.5 深入，但仍与呼吸道刺激相关。',
        },
        o3: {
            title: '臭氧 (O₃)',
            text: '地面臭氧由交通/工业气体在阳光下形成。晴天下午常较高；户外运动时可能刺激气道。',
        },
        no2: {
            title: '二氧化氮 (NO₂)',
            text: '通常与交通和燃烧相关。繁忙道路附近偏高；European 指数计算的一部分。',
        },
        so2: {
            title: '二氧化硫 (SO₂)',
            text: '来自含硫燃料燃烧和部分工业源。许多西欧城市通常低于 PM 或 NO₂。',
        },
        co: {
            title: '一氧化碳 (CO)',
            text: '不完全燃烧（交通、取暖）。室内高浓度危险；室外水平通常低得多。',
        },
        dust: {
            title: '沙尘',
            text: '矿物 / 撒哈拉沙尘负荷。峰值可在远离源区的大范围抬高 PM 值。',
        },
        uv: {
            title: 'UV 指数',
            text: '晒伤风险指数（非空气污染物）。晴天规划户外空气质量时很实用。',
        },
        aod: {
            title: 'AOD / 能见度',
            text: 'Aerosol Optical Depth 描述大气「霾」的程度。应用映射为大约能见度（km）（清晰 → 浓霾）。',
        },
        pollen: {
            title: '花粉',
            text: '过敏相关花粉量（欧洲）。按种类显示水平；图表跟踪各类别日峰值。',
        },
        disclaimer: '本页为科普内容，非医疗建议。健康决策请遵循当地官方空气质量指南。',
    },
    settings: {
        intro: {
            title: '设置概览',
            text: '在 Garmin Connect IQ（手机）或 Garmin Express 中打开应用设置。AQI 标准也可在手表菜单中更改。',
        },
        aqStd: {
            title: 'AQI 标准',
            text: 'European 或 US。影响各处数值、颜色和类别标签（主视图、图表、Glance、复杂功能）。',
        },
        pages: {
            title: '页面 1–6',
            text: '每个槽位可为：当前、24 小时预报、5 天预报、沙尘、颗粒物、霾/能见度、花粉 – 或「无」以隐藏。',
            tip: '提示：将当前放第一，再放常用预报。隐藏未用页面以减少滑动。',
        },
        series: {
            title: '颗粒物系列 A / B',
            text: '选择颗粒物对比图显示的两项指标（PM2.5、PM10、沙尘、UV、O₃、NO₂、CO、SO₂、AOD）。设为「无」可隐藏；一项时使用全宽。',
        },
        location: {
            title: '替代位置',
            text: '可选纬度 / 经度。保持 0 / 0 则使用手表位置。两者都设可固定地点（家、工作、度假地）。',
        },
        interval: {
            title: '更新间隔',
            text: '后台服务刷新频率（分钟）。最小 10，最大 720。默认 60。更短间隔消耗更多手机流量和电量。',
        },
        comp: {
            title: '复杂功能',
            forDevs: '面向开发者：',
            intro: '本应用发布可用于表盘的复杂功能（complications）',
            aqi: '"AtmosAirAQI"：当前空气质量指数（根据用户选择为欧盟或美国指数）',
            aqiRange: '包含指示该值所处位置的 “Range”。',
            ts: '"AtmosAirTs"：当前值的 UTC 时间戳',
            longLabel: 'Long Label：“AtmosAirAQI”',
        },
        limits: {
            title: '设备限制（低内存）',
            warning: '部分旧款 / 低内存型号（如 Instinct 2、Forerunner 55、Fenix 6 等）不提供额外页面（沙尘、颗粒物、霾、花粉）。当前 + 预报页面保留。',
            text: '此为有意设计，以确保小组件在这些设备的 Garmin 内存限制内稳定运行。',
        },
        supportBefore: '有问题？请使用',
        supportLink: '联系表单',
        supportAfter: '。',
    },
};
/** Alle unterstützten Sprachen */
export const AQPRO_BY_LANG = {
    en: aqproEn,
    de: aqproDe,
    fr: aqproFr,
    es: aqproEs,
    it: aqproIt,
    pt: aqproPt,
    nl: aqproNl,
    pl: aqproPl,
    cs: aqproCs,
    ru: aqproRu,
    uk: aqproUk,
    no: aqproNo,
    sv: aqproSv,
    ja: aqproJa,
    ko: aqproKo,
    zh: aqproZh,
};
